import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, FormControl,
  InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, alpha, Skeleton, Tooltip, Tabs, Tab, Collapse,
  IconButton, Autocomplete, InputAdornment
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  Settings as GearIcon,
  DeleteSweep as DeleteSweepIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  ReportProblem as ReportProblemIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

// Import our new refactored services and hooks
import { ExportService } from '../services/ExportService';
import { useForecastData } from '../hooks/useForecastData';
import { usePlanTotals } from '../hooks/usePlanTotals';
import { useUploadHandlers } from '../hooks/useUploadHandlers';
import { formatCurrency, formatCurrencyFull, formatPercentage, MONTHS } from '../utils/forecastCalculations';
import * as api from '../../../api/api';
import { buttonStyles, formControlStyles, layoutStyles } from '../../../styles/commonStyles';
import { ActionButton, BackButton, StyledFormControl, PageHeader, ElevatedCard } from '../../../components/shared/StyledComponents';

const YEAR_OPTIONS = [2023, 2024, 2025, 2026];

const ForecastVsActuals = memo(function ForecastVsActuals({
  onBackToHome,
  isReadOnly = false,
  isCompact = false,
  initialPlanId,
  availablePlans: propAvailablePlans,
  selectedDepartment = '',
}) {
  // Basic component state
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanId || '');
  const [availablePlans, setAvailablePlans] = useState(propAvailablePlans || []);
  const [year, setYear] = useState(() => {
    if (initialPlanId && propAvailablePlans) {
      const plan = propAvailablePlans.find(p => p.id === initialPlanId);
      if (plan?.year) return plan.year;
    }
    return new Date().getFullYear();
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [spendStatusFilter, setSpendStatusFilter] = useState('');
  const [expandedOutcomes, setExpandedOutcomes] = useState({});
  const [expandedDepts, setExpandedDepts] = useState({});
  const [expandedCapex, setExpandedCapex] = useState(new Set());
  const toggleCapex = (rowKey) => setExpandedCapex(prev => {
    const s = new Set(prev); s.has(rowKey) ? s.delete(rowKey) : s.add(rowKey); return s;
  });
  const [expandedVendorMonths, setExpandedVendorMonths] = useState(new Set());
  const toggleVendorMonths = (company) => setExpandedVendorMonths(prev => {
    const s = new Set(prev); s.has(company) ? s.delete(company) : s.add(company); return s;
  });
  const [expandedVendorOutcomes, setExpandedVendorOutcomes] = useState(new Set());
  const [expandedVendorMonthOutcomes, setExpandedVendorMonthOutcomes] = useState(new Set());
  const toggleVendorOutcomes = (key) => setExpandedVendorOutcomes(prev => {
    const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s;
  });
  const toggleVendorMonthOutcomes = (key) => setExpandedVendorMonthOutcomes(prev => {
    const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s;
  });
  const [planChanging, setPlanChanging] = useState(false);
  const [selectedDept, setSelectedDept] = useState(selectedDepartment || '');
  const [departments, setDepartments] = useState([]);
  const [functionalOwnerFilter, setFunctionalOwnerFilter] = useState([]);
  const [itOwnerFilter, setItOwnerFilter] = useState([]);
  const [availableOwners, setAvailableOwners] = useState({ functional: [], it: [] });
  
  const planData = useMemo(() =>
    availablePlans.find(p => p.id === selectedPlanId) || null,
    [selectedPlanId, availablePlans]
  );

  // Sync in-component dept filter when global prop changes
  useEffect(() => {
    setSelectedDept(selectedDepartment || '');
  }, [selectedDepartment]);

      // Use our new custom hooks for complex logic
  const {
    outcomes,
    loading,
    error,
    processingProgress,
    refetch,
    clearCacheAndRefresh,
    checkNetworkConnectivity
  } = useForecastData(selectedPlanId, year, planData, selectedDepartment || null);
  
  // Filter outcomes — search is intentionally excluded here so plan summary totals are unaffected by search
  const filteredOutcomes = useMemo(() => {
    return outcomes.filter(outcome => {
      if (spendStatusFilter && outcome.spendStatus !== spendStatusFilter) {
        return false;
      }
      if (functionalOwnerFilter.length > 0 && !functionalOwnerFilter.includes(outcome.functional_owner || '')) {
        return false;
      }
      if (itOwnerFilter.length > 0 && !itOwnerFilter.includes(outcome.it_owner || '')) {
        return false;
      }
      return true;
    });
  }, [outcomes, spendStatusFilter, functionalOwnerFilter, itOwnerFilter]);

  // Department-filtered budget totals (for header summary when dept is selected)
  const deptBudgetTotals = useMemo(() => {
    if (!selectedDept) return null;
    let capex = 0, opex = 0, deptProjected = 0, totalProjected = 0;
    // Use filteredOutcomes (not deptFilteredOutcomes) so total includes all outcomes, not just those with staffing
    filteredOutcomes.forEach(outcome => {
      const deptRow = (outcome.department_budgets || []).find(db => db.department_name === selectedDept);
      if (deptRow) {
        capex += deptRow.capex || 0;
        opex += deptRow.opex || 0;
      }
      deptProjected += outcome.deptProjected?.[selectedDept]?.totalProjectedCost || 0;
      totalProjected += outcome.totalProjectedCost || 0;
    });
    const remaining = (capex + opex) - deptProjected;
    const pctOfTotal = totalProjected > 0 ? (deptProjected / totalProjected) * 100 : 0;
    return { capex, opex, total: capex + opex, deptProjected, remaining, pctOfTotal };
  }, [filteredOutcomes, selectedDept]);

  // When a dept is selected, restrict to outcomes that have budget or staffing for that dept.
  // Synthetic (_unmatched) and out-of-plan outcomes always pass through so upload totals stay complete.
  const deptFilteredOutcomes = useMemo(() => {
    if (!selectedDept) return filteredOutcomes;
    return filteredOutcomes.filter(o =>
      o.isSynthetic ||
      o.isOutOfPlan ||
      (o.deptBreakdown?.[selectedDept] && Object.keys(o.deptBreakdown[selectedDept]).length > 0) ||
      (o.department_budgets || []).some(db => db.department_name === selectedDept)
    );
  }, [filteredOutcomes, selectedDept]);

  // Calculate plan totals using our custom hook — pass selectedDept for dept-scoped budget/projected figures
  const { planTotals, planStatusAssessment } = usePlanTotals(deptFilteredOutcomes, selectedDept || null);

  // Search applied last — only narrows the visible outcome list, never affects summary totals
  const visibleOutcomes = useMemo(() => {
    if (!searchQuery) return deptFilteredOutcomes;
    const query = searchQuery.toLowerCase();
    return deptFilteredOutcomes.filter(o =>
      o.name.toLowerCase().includes(query) ||
      o.project_code?.toLowerCase().includes(query)
    );
  }, [deptFilteredOutcomes, searchQuery]);

  // Portfolio summary state
  const [portfolioTabIndex, setPortfolioTabIndex] = useState(0);
  const [portfolioExpanded, setPortfolioExpanded] = useState(true);

  // Portfolio dept summary: approved vs projected per department across all filteredOutcomes
  const portfolioDeptSummary = useMemo(() => {
    const cutoff = planTotals.ytdCutoffMonth; // -1 means no YTD
    const summary = {};
    const initDept = () => ({
      approved: 0, approvedCapex: 0, approvedOpex: 0,
      projected: 0, projectedCapex: 0, projectedOpex: 0,
      ytdProjected: 0, ytdProjectedCapex: 0, ytdProjectedOpex: 0,
      ytdActual: 0, ytdActualCapex: 0, ytdActualOpex: 0,
      monthlyProjected: Array(12).fill(0),
      monthlyActual: Array(12).fill(0),
      monthlyActualCapex: Array(12).fill(0),
      monthlyActualOpex: Array(12).fill(0),
      eoy: 0,
    });

    filteredOutcomes.forEach(outcome => {
      // Approved budgets (with capex/opex split)
      (outcome.department_budgets || []).forEach(db => {
        const dept = db.department_name;
        if (!summary[dept]) summary[dept] = initDept();
        summary[dept].approved    += (db.capex || 0) + (db.opex || 0);
        summary[dept].approvedCapex += (db.capex || 0);
        summary[dept].approvedOpex  += (db.opex  || 0);
      });

      // Projected per dept — deptProjected already has official CA blended in
      Object.entries(outcome.deptProjected || {}).forEach(([dept, data]) => {
        if (!summary[dept]) summary[dept] = initDept();
        const monthly = data.monthlyProjected || Array(12).fill(0);
        const monthlyCapex = data.monthlyCapex || Array(12).fill(0);
        const monthlyOpex  = data.monthlyOpex  || Array(12).fill(0);
        const deptCapexTotal = data.totalCapex || 0;
        const deptOpexTotal  = data.totalOpex  || 0;
        const deptProjTotal  = data.totalProjectedCost || 1;

        let deptTotal = 0;
        let deptYtdProj = 0, deptYtdCapex = 0, deptYtdOpex = 0;
        for (let i = 0; i < 12; i++) {
          const v = Math.max(0, monthly[i] || 0);
          summary[dept].monthlyProjected[i] += v;
          deptTotal += v;
          if (cutoff >= 0 && i <= cutoff) {
            deptYtdProj  += v;
            // Apportion capex/opex proportionally if monthly splits not available
            const share = deptProjTotal > 0 ? v / deptProjTotal : 0;
            deptYtdCapex += monthlyCapex[i] > 0 ? monthlyCapex[i] : deptCapexTotal * share;
            deptYtdOpex  += monthlyOpex[i]  > 0 ? monthlyOpex[i]  : deptOpexTotal  * share;
          }
        }
        summary[dept].projected        += deptTotal;
        summary[dept].ytdProjected     += deptYtdProj;
        summary[dept].ytdProjectedCapex += deptYtdCapex;
        summary[dept].ytdProjectedOpex  += deptYtdOpex;
        summary[dept].projectedCapex    += deptCapexTotal;
        summary[dept].projectedOpex     += deptOpexTotal;
      });

      // YTD Actual via upload department tag
      if (outcome.spendDepartment && outcome.monthlyActual) {
        const dept = outcome.spendDepartment;
        if (!summary[dept]) summary[dept] = initDept();
        // Derive per-month capex/opex split from company actuals ratio
        const totalActual = outcome.totalActualSpend || 0;
        const actualCapexRatio = totalActual > 0 ? (outcome.totalActualCapex || 0) / totalActual : 0;
        const actualOpexRatio  = totalActual > 0 ? (outcome.totalActualOpex  || 0) / totalActual : 0;
        let deptYtdActual = 0, deptYtdActualCapex = 0, deptYtdActualOpex = 0;
        for (let i = 0; i < 12; i++) {
          const v = outcome.monthlyActual[i] || 0;
          summary[dept].monthlyActual[i]      += v;
          summary[dept].monthlyActualCapex[i] += v * actualCapexRatio;
          summary[dept].monthlyActualOpex[i]  += v * actualOpexRatio;
          if (cutoff >= 0 && i <= cutoff) {
            deptYtdActual      += v;
            deptYtdActualCapex += v * actualCapexRatio;
            deptYtdActualOpex  += v * actualOpexRatio;
          }
        }
        summary[dept].ytdActual      += deptYtdActual;
        summary[dept].ytdActualCapex += deptYtdActualCapex;
        summary[dept].ytdActualOpex  += deptYtdActualOpex;
      }
    });

    return Object.entries(summary)
      .map(([dept, d]) => {
        let eoy = 0;
        for (let i = 0; i < 12; i++) {
          eoy += cutoff >= 0 && i <= cutoff
            ? (d.monthlyActual[i] || 0)
            : (d.monthlyProjected[i] || 0);
        }
        return {
          dept, ...d, eoy,
          variance: d.projected - d.approved,
          varianceCapex: d.projectedCapex - d.approvedCapex,
          varianceOpex:  d.projectedOpex  - d.approvedOpex,
          ytdVariance:      d.ytdActual      - d.ytdProjected,
          ytdVarianceCapex: d.ytdActualCapex - d.ytdProjectedCapex,
          ytdVarianceOpex:  d.ytdActualOpex  - d.ytdProjectedOpex,
        };
      })
      .sort((a, b) => b.projected - a.projected);
  }, [filteredOutcomes, planTotals.ytdCutoffMonth]);

  // Portfolio vendor summary: projected vs actual per vendor/company across all filteredOutcomes
  const portfolioVendorSummary = useMemo(() => {
    const cutoff = planTotals.ytdCutoffMonth;
    const summary = {};
    const hasAnyOfficial = filteredOutcomes.some(o => o.hasOfficialForecast);
    const initVendor = () => ({
      projected: 0, projectedCapex: 0, projectedOpex: 0,
      official: 0, actual: 0, actualCapex: 0, actualOpex: 0,
      ytdProjected: 0, ytdProjectedCapex: 0, ytdProjectedOpex: 0,
      ytdActual: 0, ytdActualCapex: 0, ytdActualOpex: 0,
      monthlyProjected: Array(12).fill(0),
      monthlyActual: Array(12).fill(0),
      monthlyOfficial: Array(12).fill(0)
    });
    filteredOutcomes.forEach(outcome => {
      Object.entries(outcome.companyBreakdown || {}).forEach(([company, data]) => {
        if (!summary[company]) summary[company] = initVendor();
        const projData = outcome.companyStaffingBreakdown?.[company] || data;
        summary[company].projected     += projData.total || 0;
        summary[company].projectedCapex += projData.capex || 0;
        summary[company].projectedOpex  += projData.opex  || 0;
        const mf = projData.monthlyForecast || [];
        const mc = projData.monthlyCapex    || [];
        const mo = projData.monthlyOpex     || [];
        const projTotal = projData.total || 1;
        for (let i = 0; i < 12; i++) {
          summary[company].monthlyProjected[i] += mf[i] || 0;
          if (cutoff >= 0 && i <= cutoff) {
            summary[company].ytdProjected     += mf[i] || 0;
            // Use monthly capex/opex arrays if populated, else apportion by ratio
            const share = projTotal > 0 ? (mf[i] || 0) / projTotal : 0;
            summary[company].ytdProjectedCapex += mc[i] > 0 ? mc[i] : (projData.capex || 0) * share;
            summary[company].ytdProjectedOpex  += mo[i] > 0 ? mo[i] : (projData.opex  || 0) * share;
          }
        }
      });
      Object.entries(outcome.companyOfficialForecast || {}).forEach(([company, data]) => {
        if (!summary[company]) summary[company] = initVendor();
        summary[company].official += data.total || 0;
        const mo = data.monthlyForecast || [];
        for (let i = 0; i < 12; i++) summary[company].monthlyOfficial[i] += mo[i] || 0;
      });
      Object.entries(outcome.companyActuals || {}).forEach(([company, data]) => {
        if (!summary[company]) summary[company] = initVendor();
        summary[company].actual      += data.total || 0;
        summary[company].actualCapex += data.capex || 0;
        summary[company].actualOpex  += data.opex  || 0;
        const ma = outcome.companyMonthlyActuals?.[company] || [];
        const actTotal = data.total || 1;
        for (let i = 0; i < 12; i++) {
          summary[company].monthlyActual[i] += ma[i] || 0;
          if (cutoff >= 0 && i <= cutoff) {
            summary[company].ytdActual += ma[i] || 0;
            const share = actTotal > 0 ? (ma[i] || 0) / actTotal : 0;
            summary[company].ytdActualCapex += (data.capex || 0) * share;
            summary[company].ytdActualOpex  += (data.opex  || 0) * share;
          }
        }
      });
    });
    return Object.entries(summary)
      .map(([company, d]) => {
        // EOY: actuals through cutoff, projected for remainder
        let eoy = 0, eoyCapex = 0, eoyOpex = 0;
        const actTotal  = d.actual    || 1;
        const projTotal = d.projected || 1;
        const actCapexRatio  = d.actual    > 0 ? d.actualCapex    / actTotal  : 0;
        const actOpexRatio   = d.actual    > 0 ? d.actualOpex     / actTotal  : 0;
        const projCapexRatio = d.projected > 0 ? d.projectedCapex / projTotal : 0;
        const projOpexRatio  = d.projected > 0 ? d.projectedOpex  / projTotal : 0;
        for (let i = 0; i < 12; i++) {
          if (cutoff >= 0 && i <= cutoff) {
            const v = d.monthlyActual[i] || 0;
            eoy      += v;
            eoyCapex += v * actCapexRatio;
            eoyOpex  += v * actOpexRatio;
          } else {
            const v = d.monthlyProjected[i] || 0;
            eoy      += v;
            eoyCapex += v * projCapexRatio;
            eoyOpex  += v * projOpexRatio;
          }
        }
        const ytdVariance      = d.actual > 0 ? d.ytdActual      - d.ytdProjected      : null;
        const ytdVarianceCapex = d.actual > 0 ? d.ytdActualCapex - d.ytdProjectedCapex : null;
        const ytdVarianceOpex  = d.actual > 0 ? d.ytdActualOpex  - d.ytdProjectedOpex  : null;
        return {
          company, ...d, hasAnyOfficial,
          eoy, eoyCapex, eoyOpex,
          ytdVariance, ytdVarianceCapex, ytdVarianceOpex,
        };
      })
      .filter(d => d.projected > 0 || d.actual > 0)
      .sort((a, b) => b.projected - a.projected);
  }, [filteredOutcomes]);

    // Upload handlers using our custom hook
  const planOutcomeIds = useMemo(() => outcomes.map(o => o.id), [outcomes]);
  const uploadHandlers = useUploadHandlers(checkNetworkConnectivity, clearCacheAndRefresh, year, planOutcomeIds);

  // Create export service instance
  const exportService = useMemo(() => new ExportService(), []);

    // Helper functions
  const getSelectedPlanName = useCallback(() => {
    if (!selectedPlanId) return 'All Approved/Complete Outcomes';
    const plan = availablePlans.find(p => p.id === selectedPlanId);
    return plan ? plan.name : 'Selected Plan';
  }, [selectedPlanId, availablePlans]);

  const STATUS_DEFINITIONS = {
    'on-track':        'On Track — Projected forecast is within approved budget.',
    'at-risk':         'At Risk — YTD actuals exceed YTD projection, but full-year forecast is still within budget.',
    'needs-attention': 'Needs Attention — YTD actuals exceed YTD projection AND full-year forecast exceeds approved budget.',
    'off-track':       'Off Track — Projected forecast exceeds approved budget.',
    // legacy values
    'on-target':       'On Track — Projected forecast is within approved budget.',
    'underspent':      'On Track — Projected forecast is within approved budget.',
    'overspent':       'Off Track — Projected forecast exceeds approved budget.',
  };

  const getSpendStatusIcon = useCallback((status) => {
    switch (status) {
      case 'on-track':        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'at-risk':         return <WarningAmberIcon fontSize="small" color="warning" />;
      case 'needs-attention': return <ReportProblemIcon fontSize="small" color="error" />;
      case 'off-track':       return <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.dark' }} />;
      default:                return <CheckCircleIcon fontSize="small" color="success" />;
    }
  }, []);

  const getSpendStatusColor = useCallback((status) => {
    switch (status) {
      case 'on-track':        return 'success';
      case 'at-risk':         return 'warning';
      case 'needs-attention': return 'error';
      case 'off-track':       return 'error';
      default:                return 'success';
    }
  }, []);

  const handleToggleExpand = useCallback((outcomeId) => {
    setExpandedOutcomes(prev => ({
      ...prev,
      [outcomeId]: !prev[outcomeId]
    }));
  }, []);

  const handleExport = useCallback(() => {
    exportService.exportToExcel(
      filteredOutcomes,
      planTotals,
      getSelectedPlanName(),
      year,
      { portfolioVendorSummary, ytdCutoffMonth: planTotals.ytdCutoffMonth }
    );
  }, [exportService, filteredOutcomes, planTotals, getSelectedPlanName, year, portfolioVendorSummary]);

  const hasAnyOfficialForecast = useMemo(
    () => filteredOutcomes.some(o => o.hasOfficialForecast),
    [filteredOutcomes]
  );

  const handlePlanChange = useCallback((newPlanId) => {
    setPlanChanging(true);
    setSelectedPlanId(newPlanId);
    // planChanging will be reset by the loading state in useForecastData
  }, []);

      // Event listeners are now handled in the useForecastData hook

  // Keep internal plans list in sync when parent supplies an updated list
  useEffect(() => {
    if (propAvailablePlans?.length > 0) setAvailablePlans(propAvailablePlans);
  }, [propAvailablePlans]);

  // Fetch available plans
  useEffect(() => {
    if (propAvailablePlans && propAvailablePlans.length > 0) return;
    const fetchPlans = async () => {
      try {
        console.log('🔍 ForecastVsActuals: Starting to fetch plans...');
        console.log('🔍 API Base URL:', process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000');
        
        const response = await api.getPlans();
        console.log('🔍 ForecastVsActuals: Raw plans response:', response);
        
        let plans = [];
        if (Array.isArray(response)) {
          plans = response;
        } else if (response.plans && Array.isArray(response.plans)) {
          plans = response.plans;
        } else if (response.data && Array.isArray(response.data)) {
          plans = response.data;
        } else {
          console.warn('🔍 ForecastVsActuals: Unexpected plans response format:', response);
          plans = [];
        }
        
        console.log('📋 ForecastVsActuals: Processed', plans.length, 'plans');
        if (plans.length > 0) {
          console.log('📋 ForecastVsActuals: Plan details:', plans.map(p => ({ 
            name: p.name, 
            id: p.id, 
            status: p.status,
            year: p.year,
            selected_outcomes: p.selected_outcomes?.length || 0,
            outcomes: p.outcomes?.length || 0
          })));
        }
        
        setAvailablePlans(plans);
        
        if (!selectedPlanId && plans.length > 0) {
          console.log('🎯 ForecastVsActuals: Auto-selecting plan from', plans.length, 'available plans...');
          const approvedPlans = plans.filter(plan => {
            const status = (plan.status || '').toLowerCase();
            return status === 'approved' || status === 'active' || status === 'published';
          });
          
          console.log('🎯 ForecastVsActuals: Found', approvedPlans.length, 'approved plans');
          
          if (approvedPlans.length > 0) {
            const sortedPlans = approvedPlans.sort((a, b) => {
              if (a.year && b.year) return b.year - a.year;
              const dateA = new Date(a.updated_at || a.created_at || 0);
              const dateB = new Date(b.updated_at || b.created_at || 0);
              return dateB - dateA;
            });
            
            console.log('🎯 ForecastVsActuals: Sorted approved plans:', sortedPlans.map(p => ({ name: p.name, year: p.year, status: p.status })));
            
            const planWithOutcomes = sortedPlans.find(plan => 
              (plan.selected_outcomes && plan.selected_outcomes.length > 0) ||
              (plan.outcomes && plan.outcomes.length > 0)
            );
            
            if (planWithOutcomes) {
              console.log('✅ ForecastVsActuals: Auto-selecting plan:', planWithOutcomes.name, 'with', 
                (planWithOutcomes.selected_outcomes?.length || planWithOutcomes.outcomes?.length || 0), 'outcomes');
              setSelectedPlanId(planWithOutcomes.id);
              if (planWithOutcomes.year) {
                setYear(planWithOutcomes.year);
              }
            } else {
              console.log('⚠️ ForecastVsActuals: No approved plan found with outcomes, trying first approved plan anyway');
              const firstApproved = sortedPlans[0];
              if (firstApproved) {
                console.log('✅ ForecastVsActuals: Auto-selecting first approved plan:', firstApproved.name);
                setSelectedPlanId(firstApproved.id);
                if (firstApproved.year) {
                  setYear(firstApproved.year);
                }
              }
            }
          } else {
            console.log('⚠️ ForecastVsActuals: No approved plans found, trying first plan anyway');
            const firstPlan = plans[0];
            if (firstPlan) {
              console.log('✅ ForecastVsActuals: Auto-selecting first available plan:', firstPlan.name, 'status:', firstPlan.status);
              setSelectedPlanId(firstPlan.id);
              if (firstPlan.year) {
                setYear(firstPlan.year);
              }
            }
          }
        } else if (plans.length === 0) {
          console.log('⚠️ ForecastVsActuals: No plans available, will show all approved/complete outcomes');
        }
      } catch (error) {
        console.error('❌ ForecastVsActuals: Error fetching plans:', error);
        console.error('❌ ForecastVsActuals: Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        setAvailablePlans([]);
        console.log('⚠️ ForecastVsActuals: Plans fetch failed, will proceed with all outcomes');
      }
    };
    
    fetchPlans();
  }, []);

  // Fetch departments and owners in parallel
  useEffect(() => {
    Promise.all([
      api.getDepartments().catch(() => ({})),
      api.getOwners().catch(() => ({}))
    ]).then(([deptsRes, ownersRes]) => {
      setDepartments(deptsRes?.departments || (Array.isArray(deptsRes) ? deptsRes : []));
      setAvailableOwners({ functional: ownersRes?.functional || [], it: ownersRes?.it || [] });
    });
  }, []);



    // Duplicate event listeners removed - now handled in useForecastData hook

  

      

  

      






    

        









    



































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































































    // Upload handlers are now managed by the useUploadHandlers hook

    return (
    <Box sx={{ p: isCompact ? 1 : 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBackToHome}>
          Home
        </Button>
        <Typography variant="h4">Forecast vs Actuals</Typography>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Row 1: Plan, Year, Status, Dept, Func. Owner, IT Owner */}
            <Grid item xs={12} sm={6} md={3}>
              <StyledFormControl fullWidth size="small" variant="outlined">
                <InputLabel>Plan</InputLabel>
                <Select
                  value={selectedPlanId}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  disabled={loading || planChanging}
                  label="Plan"
                  renderValue={(val) => {
                    if (!val) return <em>All Outcomes</em>;
                    const plan = availablePlans.find(p => p.id === val);
                    return plan ? plan.name : val;
                  }}
                  sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                >
                  <MenuItem value=""><em>All Approved/Complete Outcomes</em></MenuItem>
                  {availablePlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} ({plan.year || 'No Year'})
                    </MenuItem>
                  ))}
                </Select>
              </StyledFormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={1}>
              <StyledFormControl fullWidth size="small" variant="outlined">
                <InputLabel>Year</InputLabel>
                <Select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  disabled={loading}
                  label="Year"
                >
                  {YEAR_OPTIONS.map((yearOption) => (
                    <MenuItem key={yearOption} value={yearOption}>{yearOption}</MenuItem>
                  ))}
                </Select>
              </StyledFormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <StyledFormControl fullWidth size="small" variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={spendStatusFilter}
                  onChange={(e) => setSpendStatusFilter(e.target.value)}
                  disabled={loading}
                  label="Status"
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="on-track">On Track</MenuItem>
                  <MenuItem value="at-risk">At Risk</MenuItem>
                  <MenuItem value="needs-attention">Needs Attention</MenuItem>
                  <MenuItem value="off-track">Off Track</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>

            {departments.length > 0 && (
              <Grid item xs={6} sm={3} md={1}>
                <StyledFormControl fullWidth size="small" variant="outlined">
                  <InputLabel>Dept</InputLabel>
                  <Select
                    value={selectedDept}
                    onChange={(e) => setSelectedDept(e.target.value)}
                    disabled={loading}
                    label="Dept"
                    renderValue={(val) => val || <em>All</em>}
                    sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                  >
                    <MenuItem value=""><em>All</em></MenuItem>
                    {departments.map(d => (
                      <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={departments.length > 0 ? 2 : 3}>
              <Autocomplete
                multiple
                size="small"
                options={availableOwners.functional}
                value={functionalOwnerFilter}
                onChange={(_, val) => setFunctionalOwnerFilter(val.map(v => typeof v === 'object' ? v.name : v))}
                getOptionLabel={(o) => (typeof o === 'object' ? o.name : o) || ''}
                isOptionEqualToValue={(o, v) => (typeof o === 'object' ? o.name : o) === (typeof v === 'object' ? v.name : v)}
                disabled={loading}
                limitTags={1}
                renderInput={(params) => (
                  <TextField {...params} label="Func. Owner" variant="outlined" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={departments.length > 0 ? 2 : 3}>
              <Autocomplete
                multiple
                size="small"
                options={availableOwners.it}
                value={itOwnerFilter}
                onChange={(_, val) => setItOwnerFilter(val.map(v => typeof v === 'object' ? v.name : v))}
                getOptionLabel={(o) => (typeof o === 'object' ? o.name : o) || ''}
                isOptionEqualToValue={(o, v) => (typeof o === 'object' ? o.name : o) === (typeof v === 'object' ? v.name : v)}
                disabled={loading}
                limitTags={1}
                renderInput={(params) => (
                  <TextField {...params} label="IT Owner" variant="outlined" />
                )}
              />
            </Grid>

          </Grid>
          
                    {/* Action Buttons Row */}
                    <Box 
            display="flex" 
            gap={2} 
            mt={3} 
            flexWrap="wrap" 
            alignItems="center"
          >
            <ActionButton
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => uploadHandlers.spendUpload.setDialogOpen(true)}
              disabled={isReadOnly || loading}
              color="primary"
              sx={{ minWidth: 180 }}
            >
              Upload Spend Data
            </ActionButton>
            
            <ActionButton
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => uploadHandlers.forecastUpload.setDialogOpen(true)}
              disabled={isReadOnly || loading}
              color="secondary"
              sx={{ minWidth: 180 }}
            >
              Upload Forecast Data
            </ActionButton>
            
            <ActionButton
              variant="outlined"
              startIcon={<GearIcon />}
              onClick={() => uploadHandlers.advanced.setDialogOpen(true)}
              disabled={isReadOnly}
              color="inherit"
              sx={{ minWidth: 160 }}
            >
              Advanced Options
            </ActionButton>
            
                        <ActionButton
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={loading || filteredOutcomes.length === 0}
              sx={{ minWidth: 120 }}
            >
              Export
            </ActionButton>

            <ActionButton
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                console.log('🔄 Manual refresh triggered');
                clearCacheAndRefresh();
              }}
              disabled={loading || processingProgress.isProcessing}
              sx={{ minWidth: 120 }}
            >
              Refresh
            </ActionButton>
            
            {process.env.NODE_ENV === 'development' && (
              <ActionButton
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  console.log('🧹 Force cache clear triggered');
                  // Clear all caches aggressively
                  if ('caches' in window) {
                    caches.keys().then(names => {
                      names.forEach(name => caches.delete(name));
                    });
                  }
                  // Clear localStorage
                  if (typeof localStorage !== 'undefined') {
                    Object.keys(localStorage).forEach(key => {
                      if (key.includes('forecast') || key.includes('spend') || key.includes('outcome')) {
                        localStorage.removeItem(key);
                      }
                    });
                  }
                  // Clear upload timestamps
                  if (typeof window !== 'undefined') {
                    delete window.lastSpendUploadTime;
                    delete window.lastForecastUploadTime;
                  }
                  setTimeout(() => clearCacheAndRefresh(), 500);
                }}
                disabled={loading || processingProgress.isProcessing}
                sx={{ minWidth: 140 }}
                color="warning"
              >
                Force Clear
              </ActionButton>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Status and Progress */}
      {(loading || processingProgress.isProcessing) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography>
                {processingProgress.isProcessing 
                  ? `Processing outcomes... ${processingProgress.current}/${processingProgress.total}`
                  : 'Loading...'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

            {/* Plan Summary */}
      {(filteredOutcomes.length > 0 || processingProgress.isProcessing) && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">
                  {processingProgress.isProcessing ? (
                    `Processing outcomes... (${processingProgress.current}/${processingProgress.total})`
                  ) : (
                    `Plan Summary (${filteredOutcomes.length} outcomes)`
                  )}
                </Typography>
                
                
              </Box>
              
              {processingProgress.isProcessing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((processingProgress.current / processingProgress.total) * 100)}%
                  </Typography>
                  <CircularProgress 
                    variant="determinate" 
                    value={(processingProgress.current / processingProgress.total) * 100} 
                    size={24} 
                  />
                </Box>
              )}
            </Box>
            
            {processingProgress.isProcessing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Loading outcome data in batches for optimal performance...
                </Typography>
                <Box sx={{ width: '100%', mt: 1 }}>
                  <div style={{ 
                    width: '100%', 
                    height: '4px', 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(processingProgress.current / processingProgress.total) * 100}%`,
                      height: '100%',
                      backgroundColor: '#4caf50',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </Box>
              </Box>
            )}
            
            {/* Department budget summary banner */}
            {selectedDept && deptBudgetTotals && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {selectedDept} Department
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Projected</Typography>
                    <Tooltip title={formatCurrencyFull(deptBudgetTotals.deptProjected)} placement="top" disableHoverListener={Math.abs(deptBudgetTotals.deptProjected) < 1_000_000}>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(deptBudgetTotals.deptProjected)}</Typography>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Approved</Typography>
                    <Tooltip title={formatCurrencyFull(deptBudgetTotals.total)} placement="top" disableHoverListener={Math.abs(deptBudgetTotals.total) < 1_000_000}>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(deptBudgetTotals.total)}</Typography>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Remaining</Typography>
                    <Tooltip title={formatCurrencyFull(Math.abs(deptBudgetTotals.remaining))} placement="top" disableHoverListener={Math.abs(deptBudgetTotals.remaining) < 1_000_000}>
                      <Typography variant="body2" fontWeight={600}
                        color={deptBudgetTotals.remaining >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(Math.abs(deptBudgetTotals.remaining))} {deptBudgetTotals.remaining >= 0 ? '✓' : '↑ over'}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">% of Total</Typography>
                    <Typography variant="body2" fontWeight={600}>{deptBudgetTotals.pctOfTotal.toFixed(1)}%</Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {(() => {
              // Shared helpers for YTD-aware cards
              const hasYtd = planTotals.ytdCutoffMonth >= 0;
              const ytdCutoff = planTotals.ytdCutoffMonth;
              const ytdBaseline = planTotals.ytdOfficialForecastBudget > 0
                ? planTotals.ytdOfficialForecastBudget
                : planTotals.ytdProjectedBudget;
              const ytdVariance = planTotals.ytdActualBudget - ytdBaseline;
              const ytdVarianceIsOver = ytdVariance > 0;

              const EoyPortfolioTooltipContent = ({ cutoff, monthlyActual, eoyCapex, eoyOpex, vendorRows, eoyTotal, ppmEoyTotal }) => {
                if (cutoff < 0) return <Box sx={{ minWidth: 200, py: 0.5 }}><Typography variant="caption">No actuals uploaded yet.</Typography></Box>;
                const remainingMonths = Math.max(1, 11 - cutoff);
                const vendorRemainders = vendorRows
                  .map(r => ({
                    name: r.company,
                    monthlyAmount: r.monthlyProjected.slice(cutoff + 1).reduce((s, v) => s + (v || 0), 0) / remainingMonths,
                  }))
                  .filter(r => r.monthlyAmount > 0);
                return (
                  <Box sx={{ minWidth: 230, py: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>EOY Projection</Typography>
                    {(eoyCapex > 0 || eoyOpex > 0) && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.85 }}>
                        C: {formatCurrencyFull(eoyCapex)} | O: {formatCurrencyFull(eoyOpex)}
                      </Typography>
                    )}
                    {ppmEoyTotal > 0 && ppmEoyTotal !== eoyTotal && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.75, fontStyle: 'italic' }}>
                        PPM projection: {formatCurrencyFull(ppmEoyTotal)}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.25, opacity: 0.9 }}>
                      YTD Actuals (through {MONTHS[cutoff]})
                    </Typography>
                    {MONTHS.slice(0, cutoff + 1).map((m, i) => (
                      <Box key={m} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{m}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrencyFull(monthlyActual[i] || 0)}</Typography>
                      </Box>
                    ))}
                    {cutoff < 11 && vendorRemainders.length > 0 && (
                      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.25, opacity: 0.9 }}>
                          Remainder ({MONTHS[cutoff + 1]}–Dec)
                        </Typography>
                        {vendorRemainders.map(({ name, monthlyAmount }) => (
                          <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>{name}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrencyFull(monthlyAmount)}/mo</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              };

              const MonthlyTooltipContent = ({ title, capex, opex, monthlyData, limitToYtd, totalOverride }) => {
                const total = totalOverride != null ? totalOverride : ((capex || 0) + (opex || 0));
                return (
                <Box sx={{ minWidth: 200, py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>{title}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.25, opacity: 0.85 }}>
                    Total: {formatCurrencyFull(total)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.85 }}>
                    C: {formatCurrencyFull(capex)} | O: {formatCurrencyFull(opex)}
                  </Typography>
                  <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5, mt: 0.5 }}>
                    {MONTHS.slice(0, limitToYtd ? ytdCutoff + 1 : 12).map((m, i) => (
                      <Box key={m} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{m}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrencyFull(monthlyData[i] || 0)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                );
              };

              const eoyVsPpm = planTotals.eoyLandingEstimate - planTotals.totalForecastBudget;
              const eoyVsPpmIsOver = eoyVsPpm > 0;
              const eoyVsBudget = planTotals.eoyLandingEstimate - planTotals.totalApprovedBudget;
              const eoyVsBudgetIsOver = eoyVsBudget > 0;
              const ytdVsPpm = planTotals.ytdActualBudget - planTotals.ytdProjectedBudget;
              const ytdVsPpmIsOver = ytdVsPpm > 0;

              return (
            <>
            <Grid container spacing={1.5}>
              {/* Approved Budget */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Tooltip title={<Box sx={{ minWidth: 200, py: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Approved Budget ({planTotals.outcomesWithApprovedBudget} outcomes)</Typography><Typography variant="caption" sx={{ display: 'block', mb: 0.25, opacity: 0.85 }}>Total: {formatCurrencyFull(planTotals.totalApprovedBudget)}</Typography><Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>C: {formatCurrencyFull(planTotals.totalApprovedCapex)} | O: {formatCurrencyFull(planTotals.totalApprovedOpex)}</Typography></Box>} arrow>
                  <Card variant="outlined" sx={{ p: 1.5, bgcolor: alpha('#2196f3', 0.1), height: isCompact ? 72 : 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'help' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>Approved Budget</Typography>
                    {(loading || processingProgress.isProcessing) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Skeleton width={120} height={32} />
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6" color="primary.main" fontWeight={600}>
                          {formatCurrency(planTotals.totalApprovedBudget)}
                        </Typography>
                        {!isCompact && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, fontSize: '0.75rem' }}>
                            {planTotals.outcomesWithApprovedBudget} outcomes
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Card>
                </Tooltip>
              </Grid>

              {/* PPM Projection */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Tooltip title={<MonthlyTooltipContent title={`PPM Projection (${planTotals.outcomesWithProjectedForecast} outcomes)`} capex={planTotals.totalForecastCapex} opex={planTotals.totalForecastOpex} monthlyData={planTotals.planMonthlyProjected} limitToYtd={false} totalOverride={planTotals.totalForecastBudget} />} arrow>
                  <Card variant="outlined" sx={{ p: 1.5, bgcolor: alpha('#4caf50', 0.1), height: isCompact ? 72 : 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'help' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>PPM Projection</Typography>
                    {(loading || processingProgress.isProcessing) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Skeleton width={120} height={32} />
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6" color="success.main" fontWeight={600}>
                          {formatCurrency(planTotals.totalForecastBudget)}
                        </Typography>
                        {!isCompact && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, fontSize: '0.75rem' }}>
                            {planTotals.outcomesWithProjectedForecast} outcomes
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Card>
                </Tooltip>
              </Grid>

              {/* YTD Actuals */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Tooltip title={<MonthlyTooltipContent title={`YTD Actual (${planTotals.outcomesWithActualSpend} outcomes)`} capex={planTotals.totalActualCapex} opex={planTotals.totalActualOpex} monthlyData={planTotals.planMonthlyActual} limitToYtd={hasYtd} totalOverride={hasYtd ? planTotals.ytdActualBudget : planTotals.totalActualBudget} />} arrow>
                  <Card variant="outlined" sx={{ p: 1.5, bgcolor: alpha('#ff9800', 0.1), height: isCompact ? 72 : 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'help' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>YTD Actuals</Typography>
                    {(loading || processingProgress.isProcessing) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Skeleton width={120} height={32} />
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6" color="warning.main" fontWeight={600}>
                          {formatCurrency(hasYtd ? planTotals.ytdActualBudget : planTotals.totalActualBudget)}
                        </Typography>
                        {!isCompact && hasYtd && planTotals.ytdProjectedBudget > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.3, fontSize: '0.75rem', color: ytdVsPpmIsOver ? 'error.main' : 'success.main' }}>
                            PPM: {formatCurrency(planTotals.ytdProjectedBudget)} {ytdVsPpmIsOver ? '↑' : '↓'} {formatCurrency(Math.abs(ytdVsPpm))} {ytdVsPpmIsOver ? 'over' : 'under'}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Card>
                </Tooltip>
              </Grid>

              {/* EOY Projection */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Tooltip arrow title={<EoyPortfolioTooltipContent
                  cutoff={planTotals.ytdCutoffMonth}
                  monthlyActual={planTotals.planMonthlyActual}
                  eoyCapex={portfolioVendorSummary.reduce((s, r) => s + (r.eoyCapex || 0), 0)}
                  eoyOpex={portfolioVendorSummary.reduce((s, r) => s + (r.eoyOpex || 0), 0)}
                  vendorRows={portfolioVendorSummary}
                  eoyTotal={planTotals.eoyLandingEstimate}
                  ppmEoyTotal={planTotals.ppmEoyLandingEstimate}
                />}>
                  <Card variant="outlined" sx={{ p: 1.5, bgcolor: alpha('#0288d1', 0.1), height: isCompact ? 72 : 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'help' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>EOY Projection</Typography>
                    {(loading || processingProgress.isProcessing) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Skeleton width={120} height={32} />
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6" color="info.main" fontWeight={600}>
                          {formatCurrency(planTotals.eoyLandingEstimate)}
                        </Typography>
                        {!isCompact && planTotals.totalForecastBudget > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.3, fontSize: '0.75rem', color: eoyVsPpmIsOver ? 'error.main' : 'success.main' }}>
                            {eoyVsPpmIsOver ? '↑' : '↓'} {formatCurrency(Math.abs(eoyVsPpm))} vs PPM
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Card>
                </Tooltip>
              </Grid>

              {/* vs Approved Budget */}
              <Grid item xs={12} sm={6} md={2.4}>
                <Tooltip title={<Box sx={{ minWidth: 200, py: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>EOY vs Approved Budget</Typography><Typography variant="caption" sx={{ display: 'block', mb: 0.25, opacity: 0.85 }}>EOY Projection: {formatCurrencyFull(planTotals.eoyLandingEstimate)}</Typography><Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>Approved Budget: {formatCurrencyFull(planTotals.totalApprovedBudget)}</Typography></Box>} arrow>
                  <Card variant="outlined" sx={{ p: 1.5, bgcolor: eoyVsBudgetIsOver ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1), height: isCompact ? 72 : 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'help' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>vs Approved Budget</Typography>
                    {(loading || processingProgress.isProcessing) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Skeleton width={120} height={32} />
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6" sx={{ color: eoyVsBudgetIsOver ? 'error.main' : 'success.main' }} fontWeight={600}>
                          {eoyVsBudget >= 0 ? '+' : ''}{formatCurrency(eoyVsBudget)}
                        </Typography>
                        {!isCompact && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, fontSize: '0.75rem' }}>
                            {eoyVsBudgetIsOver ? 'over budget' : 'under budget'}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>
            </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Breakdown tabs — merged into plan summary card above via inline tabs */}
      {!loading && (portfolioDeptSummary.length > 0 || portfolioVendorSummary.length > 0) && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: portfolioExpanded ? 1 : 0 }}>
              <Tabs value={portfolioTabIndex} onChange={(_, v) => setPortfolioTabIndex(v)} sx={{ minHeight: 36 }}>
                {portfolioDeptSummary.length > 0 && <Tab label="By Department" sx={{ minHeight: 36, py: 0 }} />}
                {portfolioVendorSummary.length > 0 && <Tab label="By Vendor" sx={{ minHeight: 36, py: 0 }} />}
              </Tabs>
              <IconButton size="small" onClick={() => setPortfolioExpanded(v => !v)}>
                {portfolioExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
            <Collapse in={portfolioExpanded}>
              {/* By Department tab */}
              {portfolioDeptSummary.length > 0 && portfolioTabIndex === 0 && (() => {
                const hasYtd = planTotals.ytdCutoffMonth >= 0;
                const hasAnyActual = portfolioDeptSummary.some(r => r.ytdActual > 0);
                const stackedCell = (total, capex, opex, opts = {}) => {
                  if (!total && total !== 0) return '—';
                  const { color, prefix } = opts;
                  return (
                    <Tooltip title={`C: ${formatCurrencyFull(capex || 0)} | O: ${formatCurrencyFull(opex || 0)}`} placement="top" arrow>
                      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', cursor: 'help', ...(color ? { color } : {}) }}>
                        {prefix}{formatCurrency(total)}
                      </Typography>
                    </Tooltip>
                  );
                };
                const varColor = (val) => val > 0 ? 'error.main' : val < 0 ? 'success.main' : undefined;
                const hdrCell = (label, tip, align = 'right') => (
                  <Tooltip title={tip} placement="top" arrow>
                    <TableCell align={align} sx={{ whiteSpace: 'nowrap', cursor: 'help' }}>{label}</TableCell>
                  </Tooltip>
                );
                return (
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Department</TableCell>
                          {hdrCell('Approved', 'Sum of approved capex + opex budget allocations from all outcomes assigned to this department.')}
                          {hdrCell('PPM Proj', 'Full-year staffing cost forecast for this department.')}
                          {hdrCell('Variance', 'PPM Projected minus Approved. Red = over budget, green = under budget.')}
                          {hasYtd && hdrCell('EOY Proj', 'Full-year landing estimate: actuals through the latest month, PPM projection for remaining months.')}
                          {hasYtd && hasAnyActual && hdrCell('YTD Actual', 'Actual spend uploaded for this department through the latest uploaded month.')}
                          {hasYtd && hdrCell('YTD Proj', 'Projected spend for this department through the same cutoff month as the latest actuals.')}
                          {hasYtd && hasAnyActual && hdrCell('YTD Variance', 'YTD Actual minus YTD Projected. Red = over projection, green = under projection.')}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {portfolioDeptSummary.map(row => (
                          <TableRow key={row.dept}>
                            <TableCell>{row.dept}</TableCell>
                            <TableCell align="right">
                              {row.approved > 0 ? stackedCell(row.approved, row.approvedCapex, row.approvedOpex) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {stackedCell(row.projected, row.projectedCapex, row.projectedOpex)}
                            </TableCell>
                            <TableCell align="right">
                              {row.approved > 0
                                ? stackedCell(row.variance, row.varianceCapex, row.varianceOpex, { color: varColor(row.variance), prefix: row.variance >= 0 ? '+' : '' })
                                : '—'}
                            </TableCell>
                            {hasYtd && (
                              <TableCell align="right">
                                {row.eoy > 0 ? stackedCell(row.eoy, 0, 0) : '—'}
                              </TableCell>
                            )}
                            {hasYtd && hasAnyActual && (
                              <TableCell align="right">
                                {row.ytdActual > 0 ? stackedCell(row.ytdActual, row.ytdActualCapex, row.ytdActualOpex) : '—'}
                              </TableCell>
                            )}
                            {hasYtd && (
                              <TableCell align="right">
                                {row.ytdProjected > 0 ? stackedCell(row.ytdProjected, row.ytdProjectedCapex, row.ytdProjectedOpex) : '—'}
                              </TableCell>
                            )}
                            {hasYtd && hasAnyActual && (
                              <TableCell align="right">
                                {(row.ytdActual > 0 || row.ytdProjected > 0)
                                  ? stackedCell(row.ytdVariance, row.ytdVarianceCapex, row.ytdVarianceOpex, { color: varColor(row.ytdVariance), prefix: row.ytdVariance >= 0 ? '+' : '' })
                                  : '—'}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}

              {/* By Vendor tab */}
              {portfolioVendorSummary.length > 0 && portfolioTabIndex === (portfolioDeptSummary.length > 0 ? 1 : 0) && (() => {
                const hasYtd = planTotals.ytdCutoffMonth >= 0;
                const showOfficial = portfolioVendorSummary.some(r => r.official > 0);
                const hasAnyActual = portfolioVendorSummary.some(r => r.actual > 0);
                const stackedCell = (total, capex, opex, opts = {}) => {
                  if (total === null || total === undefined) return '—';
                  const { color, prefix = '' } = opts;
                  return (
                    <Tooltip title={`C: ${formatCurrencyFull(capex || 0)} | O: ${formatCurrencyFull(opex || 0)}`} placement="top" arrow>
                      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', cursor: 'help', ...(color ? { color } : {}) }}>
                        {prefix}{formatCurrency(total)}
                      </Typography>
                    </Tooltip>
                  );
                };
                const varColor = (val) => val > 0 ? 'error.main' : val < 0 ? 'success.main' : undefined;
                const colW = { width: 80, minWidth: 72, maxWidth: 96 };
                const hdrCell = (line1, line2, tip, align = 'right') => (
                  <Tooltip title={tip} placement="top" arrow>
                    <TableCell align={align} sx={{ ...colW, fontSize: '0.7rem', lineHeight: 1.2, verticalAlign: 'bottom', pb: 0.5, cursor: 'help' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span>{line1}</span>
                        <span>{line2}</span>
                      </Box>
                    </TableCell>
                  </Tooltip>
                );
                return (
                  <Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 32, p: 0 }} />
                          <TableCell sx={{ fontSize: '0.75rem' }}>Vendor / Company</TableCell>
                          {hdrCell('EOY', 'Proj', 'Full-year landing estimate: actuals through the latest uploaded month, staffing projection for remaining months.')}
                          {hdrCell('PPM', 'Proj', 'Full-year staffing cost forecast for this vendor from the staffing model.')}
                          {showOfficial && hdrCell('Finance', 'Forecast', 'Full-year official Finance forecast uploaded for this vendor.')}
                          {showOfficial && hdrCell('Finance', 'vs PPM', 'Finance Forecast minus PPM Projection. Red = Finance higher than PPM, green = Finance lower.')}
                          {hasAnyActual && hdrCell('YTD', 'Actual', 'Actual spend uploaded for this vendor through the latest uploaded month.')}
                          {hasYtd && hdrCell('YTD', 'Proj', 'Staffing projection for this vendor through the same cutoff month as the latest actuals.')}
                          {hasAnyActual && hasYtd && hdrCell('YTD', 'Var', 'YTD Actual minus YTD Projected. Red = over projection, green = under projection.')}
                          <TableCell sx={{ width: 36, p: 0 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {portfolioVendorSummary.map(row => (
                          <React.Fragment key={row.company}>
                            <TableRow>
                              <TableCell sx={{ width: 32, p: 0 }}>
                                <IconButton size="small" onClick={() => toggleVendorMonths(row.company)}>
                                  {expandedVendorMonths.has(row.company)
                                    ? <KeyboardArrowUpIcon fontSize="small" />
                                    : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', overflow: 'hidden' }}>
                                <Tooltip title={row.company} placement="top" arrow disableHoverListener={row.company.length <= 25}>
                                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.company}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right" sx={colW}>
                                {row.eoy > 0 ? stackedCell(row.eoy, row.eoyCapex, row.eoyOpex) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={colW}>
                                {stackedCell(row.projected, row.projectedCapex, row.projectedOpex)}
                              </TableCell>
                              {showOfficial && (
                                <TableCell align="right" sx={colW}>
                                  {row.official > 0 ? stackedCell(row.official, 0, 0) : '—'}
                                </TableCell>
                              )}
                              {showOfficial && (() => {
                                const delta = row.official > 0 ? row.official - row.projected : null;
                                return (
                                  <TableCell align="right" sx={colW}>
                                    {delta !== null
                                      ? stackedCell(delta, 0, 0, { color: delta > 0 ? 'error.main' : 'success.main', prefix: delta >= 0 ? '+' : '' })
                                      : '—'}
                                  </TableCell>
                                );
                              })()}
                              {hasAnyActual && (
                                <TableCell align="right" sx={colW}>
                                  {row.actual > 0 ? stackedCell(row.actual, row.actualCapex, row.actualOpex) : '—'}
                                </TableCell>
                              )}
                              {hasYtd && (
                                <TableCell align="right" sx={colW}>
                                  {row.ytdProjected > 0 ? stackedCell(row.ytdProjected, row.ytdProjectedCapex, row.ytdProjectedOpex) : '—'}
                                </TableCell>
                              )}
                              {hasAnyActual && hasYtd && (
                                <TableCell align="right" sx={colW}>
                                  {row.ytdVariance !== null
                                    ? stackedCell(row.ytdVariance, row.ytdVarianceCapex, row.ytdVarianceOpex, { color: varColor(row.ytdVariance), prefix: row.ytdVariance >= 0 ? '+' : '' })
                                    : '—'}
                                </TableCell>
                              )}
                              <TableCell sx={{ width: 36, p: 0 }} />
                            </TableRow>
                            {expandedVendorMonths.has(row.company) && MONTHS.map((m, i) => {
                              const proj = row.monthlyProjected[i] || 0;
                              const act  = row.monthlyActual[i]    || 0;
                              const off  = showOfficial ? (row.monthlyOfficial[i] || 0) : 0;
                              const cutoff = planTotals.ytdCutoffMonth;
                              const eoyMonth = cutoff >= 0 && i <= cutoff ? act : proj;
                              if (proj === 0 && act === 0 && off === 0) return null;
                              return (
                                <React.Fragment key={`${row.company}-mo-${i}`}>
                                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ p: 0, width: 32 }}>
                                      <IconButton size="small" onClick={() => toggleVendorMonthOutcomes(`${row.company}-${i}`)}>
                                        {expandedVendorMonthOutcomes.has(`${row.company}-${i}`)
                                          ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                                          : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ pl: 2, fontSize: '0.75rem', color: 'text.secondary' }}>{m}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'info.main' }}>
                                      {eoyMonth > 0 ? formatCurrency(eoyMonth) : '—'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'success.main' }}>
                                      {proj > 0 ? formatCurrency(proj) : '—'}
                                    </TableCell>
                                    {showOfficial && (
                                      <TableCell align="right" sx={{ fontSize: '0.75rem', color: off > 0 ? 'secondary.main' : undefined }}>
                                        {off > 0 ? formatCurrency(off) : '—'}
                                      </TableCell>
                                    )}
                                    {showOfficial && (() => {
                                      const delta = off > 0 ? off - proj : null;
                                      return (
                                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: delta > 0 ? 'error.main' : delta < 0 ? 'success.main' : undefined }}>
                                          {delta !== null ? `${delta >= 0 ? '+' : ''}${formatCurrency(delta)}` : '—'}
                                        </TableCell>
                                      );
                                    })()}
                                    {hasAnyActual && (
                                      <TableCell align="right" sx={{ fontSize: '0.75rem', color: act > 0 ? 'warning.main' : undefined }}>
                                        {act > 0 ? formatCurrency(act) : '—'}
                                      </TableCell>
                                    )}
                                    {hasYtd && (
                                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                        {i <= cutoff ? (proj > 0 ? formatCurrency(proj) : '—') : '—'}
                                      </TableCell>
                                    )}
                                    {hasAnyActual && hasYtd && (() => {
                                      const moVar = i <= cutoff ? act - proj : null;
                                      return (
                                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: moVar > 0 ? 'error.main' : moVar < 0 ? 'success.main' : undefined }}>
                                          {moVar !== null ? `${moVar >= 0 ? '+' : ''}${formatCurrency(moVar)}` : '—'}
                                        </TableCell>
                                      );
                                    })()}
                                    <TableCell />
                                  </TableRow>
                                  {expandedVendorMonthOutcomes.has(`${row.company}-${i}`) && (() => {
                                    const companyUpper = row.company.toUpperCase();
                                    return filteredOutcomes
                                      .map(o => {
                                        const companyKey    = Object.keys(o.companyBreakdown       || {}).find(k => k.toUpperCase() === companyUpper);
                                        const actualKey     = Object.keys(o.companyActuals         || {}).find(k => k.toUpperCase() === companyUpper);
                                        const officialKey   = Object.keys(o.companyOfficialForecast|| {}).find(k => k.toUpperCase() === companyUpper);
                                        if (!companyKey && !actualKey) return null;
                                        const ck = companyKey  || row.company;
                                        const ak = actualKey   || row.company;
                                        const ok = officialKey || row.company;
                                        const oMonthlyProj = o.companyBreakdown?.[ck]?.monthlyForecast || Array(12).fill(0);
                                        const oMonthlyAct  = o.companyMonthlyActuals?.[ak]            || Array(12).fill(0);
                                        const oMonthlyOff  = o.companyOfficialForecast?.[ok]?.monthlyForecast || Array(12).fill(0);
                                        // full-year EOY: actuals through cutoff, official (or proj) for remainder
                                        let oEoy = 0;
                                        for (let mi = 0; mi < 12; mi++) {
                                          if (cutoff >= 0 && mi <= cutoff) oEoy += oMonthlyAct[mi]  || 0;
                                          else oEoy += (officialKey ? oMonthlyOff[mi] : oMonthlyProj[mi]) || 0;
                                        }
                                        const oProj    = o.companyBreakdown?.[ck]?.total            || 0;
                                        const oOfficial= o.companyOfficialForecast?.[ok]?.total     || 0;
                                        const oYtdAct  = cutoff >= 0 ? oMonthlyAct.slice(0, cutoff + 1).reduce((s, v) => s + (v || 0), 0)  : 0;
                                        const oYtdProj = cutoff >= 0 ? oMonthlyProj.slice(0, cutoff + 1).reduce((s, v) => s + (v || 0), 0) : 0;
                                        const oYtdVar  = hasAnyActual && hasYtd ? oYtdAct - oYtdProj : null;
                                        if (oProj === 0 && (o.companyActuals?.[ak]?.total || 0) === 0) return null;
                                        return (
                                          <TableRow key={`${row.company}-mo-${i}-o-${o.id}`} sx={{ bgcolor: 'action.selected' }}>
                                            <TableCell />
                                            <TableCell sx={{ pl: 6, fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', overflow: 'hidden' }}>
                                              {(() => {
                                                const label = o.name || o.project_code || o.id;
                                                return (
                                                  <Tooltip title={label} placement="top" arrow disableHoverListener={label.length <= 30}>
                                                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                                                  </Tooltip>
                                                );
                                              })()}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.7rem', color: 'info.main' }}>
                                              {oEoy > 0 ? formatCurrency(oEoy) : '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                                              {oProj > 0 ? formatCurrency(oProj) : '—'}
                                            </TableCell>
                                            {showOfficial && (
                                              <TableCell align="right" sx={{ fontSize: '0.7rem', color: 'secondary.main' }}>
                                                {oOfficial > 0 ? formatCurrency(oOfficial) : '—'}
                                              </TableCell>
                                            )}
                                            {showOfficial && (() => {
                                              const oDelta = oOfficial > 0 ? oOfficial - oProj : null;
                                              return (
                                                <TableCell align="right" sx={{ fontSize: '0.7rem', color: oDelta > 0 ? 'error.main' : oDelta < 0 ? 'success.main' : undefined }}>
                                                  {oDelta !== null ? `${oDelta >= 0 ? '+' : ''}${formatCurrency(oDelta)}` : '—'}
                                                </TableCell>
                                              );
                                            })()}
                                            {hasAnyActual && (
                                              <TableCell align="right" sx={{ fontSize: '0.7rem', color: 'warning.main' }}>
                                                {oYtdAct > 0 ? formatCurrency(oYtdAct) : '—'}
                                              </TableCell>
                                            )}
                                            {hasYtd && (
                                              <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                                                {oYtdProj > 0 ? formatCurrency(oYtdProj) : '—'}
                                              </TableCell>
                                            )}
                                            {hasAnyActual && hasYtd && (
                                              <TableCell align="right" sx={{ fontSize: '0.7rem', color: oYtdVar > 0 ? 'error.main' : oYtdVar < 0 ? 'success.main' : undefined }}>
                                                {oYtdVar !== null ? `${oYtdVar >= 0 ? '+' : ''}${formatCurrency(oYtdVar)}` : '—'}
                                              </TableCell>
                                            )}
                                            <TableCell />
                                          </TableRow>
                                        );
                                      })
                                      .filter(Boolean);
                                  })()}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </Box>
                );
              })()}
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Search bar — sits between the summary and the outcome list */}
      {!loading && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search outcomes by name or project code…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} edge="end" aria-label="clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={formControlStyles.standard}
          />
        </Box>
      )}

      {/* Outcomes List */}
      {!loading && visibleOutcomes.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Outcomes ({visibleOutcomes.length}{searchQuery ? ` matching "${searchQuery}"` : selectedDept ? ` in ${selectedDept}` : ''})
          </Typography>

          {visibleOutcomes.map((outcome) => {
            const isExpanded = expandedOutcomes[outcome.id];
            // When a dept is selected, use dept-scoped projected costs; otherwise full-plan
            const effectiveTotalProjectedCost = selectedDept
              ? (outcome.deptProjected?.[selectedDept]?.totalProjectedCost || 0)
              : outcome.totalProjectedCost;
            const effectiveMonthlyProjected = selectedDept
              ? (outcome.deptProjected?.[selectedDept]?.monthlyProjected || Array(12).fill(0))
              : outcome.monthlyProjected;
            
            return (
              <Accordion 
                key={outcome.id} 
                expanded={isExpanded}
                onChange={() => handleToggleExpand(outcome.id)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" width="100%" gap={2}>
                    <Box flex={1}>
                                            <Typography variant="h6">
                        {outcome.name}
                        {outcome.isSynthetic && (
                          <Chip label="Unmatched" size="small" color="warning" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        )}
                        {outcome.isOutOfPlan && (
                          <Chip label="Out of Plan" size="small" color="info" variant="outlined" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Project Code: {outcome.project_code || 'Not Set'}
                      </Typography>
                      {outcome.isSynthetic && (outcome.unmatchedSpendDetails?.length > 0 || outcome.unmatchedForecastDetails?.length > 0) && (
                        <Typography variant="caption" color="warning.main">
                          {[
                            outcome.unmatchedSpendDetails?.length > 0 && `Spend: ${outcome.unmatchedSpendDetails.map(d => d.project_code).join(', ')}`,
                            outcome.unmatchedForecastDetails?.length > 0 && `Forecast: ${outcome.unmatchedForecastDetails.map(d => d.project_code).join(', ')}`
                          ].filter(Boolean).join(' | ')}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box display="flex" gap={3} alignItems="center" mr={1}>
                      {(() => {
                        const hasYtd = planTotals.ytdCutoffMonth >= 0;
                        const cutoff = planTotals.ytdCutoffMonth;
                        const approvedTotal = (outcome.department_budgets || []).reduce((s, db) => s + (db.capex || 0) + (db.opex || 0), 0);
                        const ytdActual = (outcome.monthlyActual || []).slice(0, cutoff + 1).reduce((s, v) => s + (v || 0), 0);
                        const ytdProj = (effectiveMonthlyProjected || []).slice(0, cutoff + 1).reduce((s, v) => s + (v || 0), 0);
                        const ytdVar = ytdActual - ytdProj;

                        // PPM EOY: pure staffing blend (no official forecast substitution)
                        const ppmEoy = outcome.ppmEoyEstimate || 0;
                        const eoyVsPpm = (outcome.eoyEstimate || 0) - ppmEoy;

                        // Actuals cutoff: per-outcome latest month may lag the plan-wide cutoff
                        const outcomeActualsCutoff = outcome.latestActualSpendMonth ?? -1;
                        const cutoffLag = hasYtd && outcomeActualsCutoff >= 0 && outcomeActualsCutoff < cutoff;
                        const cutoffLabel = outcomeActualsCutoff >= 0
                          ? `Actuals through ${MONTHS[outcomeActualsCutoff]}`
                          : 'No actuals uploaded';

                        // CAPEX/OPEX mismatch: approved split vs actual split
                        const approvedCapex = (outcome.department_budgets || []).reduce((s, db) => s + (db.capex || 0), 0);
                        const approvedOpex  = (outcome.department_budgets || []).reduce((s, db) => s + (db.opex  || 0), 0);
                        const actualCapexRatio    = outcome.totalActualSpend > 0 ? (outcome.totalActualCapex || 0) / outcome.totalActualSpend : null;
                        const approvedCapexRatio  = approvedTotal > 0 ? approvedCapex / approvedTotal : null;
                        const coMismatch = actualCapexRatio !== null && approvedCapexRatio !== null
                          && Math.abs(actualCapexRatio - approvedCapexRatio) > 0.15;

                        const metricBox = (value, label, color, tip) => (
                          <Box textAlign="right">
                            <Tooltip title={tip || formatCurrencyFull(value)} placement="top" disableHoverListener={!tip && Math.abs(value) < 1_000_000}>
                              <Typography variant="body2" fontWeight="bold" color={color || 'text.primary'}>
                                {formatCurrency(value)}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                          </Box>
                        );
                        return (<>
                        {metricBox(approvedTotal, 'Approved')}
                        {metricBox(effectiveTotalProjectedCost, selectedDept ? `${selectedDept} Proj` : 'PPM Proj', 'success.main')}
                        {hasAnyOfficialForecast && metricBox(outcome.totalOfficialForecast || 0, 'Finance', 'secondary.main')}
                        {hasAnyOfficialForecast && hasYtd && (() => {
                          const finVsPpm = (outcome.totalOfficialForecast || 0) - effectiveTotalProjectedCost;
                          return (
                            <Box textAlign="right">
                              <Typography variant="body2" fontWeight="bold" color={finVsPpm > 0 ? 'error.main' : finVsPpm < 0 ? 'success.main' : 'text.primary'}>
                                {finVsPpm >= 0 ? '+' : ''}{formatCurrency(finVsPpm)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">Fin vs PPM</Typography>
                            </Box>
                          );
                        })()}
                        {hasYtd && metricBox(outcome.eoyEstimate || 0, 'EOY Proj', 'info.main')}
                        {hasYtd && ppmEoy > 0 && ppmEoy !== (outcome.eoyEstimate || 0) && (
                          <Box textAlign="right">
                            <Tooltip title={`PPM EOY: ${formatCurrencyFull(ppmEoy)} — pure staffing blend without official Finance forecast`} placement="top" arrow>
                              <Box>
                                <Typography variant="body2" fontWeight="bold" color={eoyVsPpm > 0 ? 'error.main' : 'success.main'}>
                                  {eoyVsPpm >= 0 ? '+' : ''}{formatCurrency(eoyVsPpm)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">EOY vs PPM</Typography>
                              </Box>
                            </Tooltip>
                          </Box>
                        )}
                        {hasYtd && metricBox(ytdActual, 'YTD Actual', 'warning.main')}
                        {hasYtd && metricBox(ytdProj, 'YTD Proj')}
                        {hasYtd && (
                          <Box textAlign="right">
                            <Typography variant="body2" fontWeight="bold" color={ytdVar > 0 ? 'error.main' : ytdVar < 0 ? 'success.main' : 'text.primary'}>
                              {ytdVar >= 0 ? '+' : ''}{formatCurrency(ytdVar)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">YTD Var</Typography>
                          </Box>
                        )}
                        {coMismatch && (
                          <Tooltip title={`CAPEX/OPEX split in actuals (${Math.round(actualCapexRatio * 100)}% C / ${Math.round((1 - actualCapexRatio) * 100)}% O) differs materially from approved budget split (${Math.round(approvedCapexRatio * 100)}% C / ${Math.round((1 - approvedCapexRatio) * 100)}% O). Check for mis-tagged transactions.`} placement="left" arrow>
                            <Chip label="C/O" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.6rem', height: 18, cursor: 'help' }} />
                          </Tooltip>
                        )}
                        {outcomeActualsCutoff >= 0 && (
                          <Tooltip title={cutoffLag
                            ? `${cutoffLabel} — ${cutoff - outcomeActualsCutoff} month(s) behind the plan-wide cutoff (${MONTHS[cutoff]}). YTD figures for this outcome are understated.`
                            : cutoffLabel
                          } placement="left" arrow>
                            <Chip
                              label={MONTHS[outcomeActualsCutoff]}
                              size="small"
                              color={cutoffLag ? 'warning' : 'default'}
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 18, cursor: 'help' }}
                            />
                          </Tooltip>
                        )}
                        </>);
                      })()}

                      <Tooltip
                        title={STATUS_DEFINITIONS[outcome.spendStatus] || outcome.spendStatusText}
                        arrow
                        placement="left"
                        enterDelay={200}
                        componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}
                      >
                        <Box
                          component="span"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: 'inline-flex' }}
                        >
                          <Chip
                            icon={getSpendStatusIcon(outcome.spendStatus)}
                            label={outcome.spendStatusText}
                            color={getSpendStatusColor(outcome.spendStatus)}
                            size="small"
                            sx={{ cursor: 'help', pointerEvents: 'none' }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  {/* Per-outcome dept metrics row */}
                  {selectedDept && (() => {
                    const deptProj = outcome.deptProjected?.[selectedDept];
                    const deptBudgetRow = (outcome.department_budgets || []).find(
                      db => db.department_name === selectedDept
                    );
                    if (!deptProj && !deptBudgetRow) return null;
                    const deptProjectedCost = deptProj?.totalProjectedCost || 0;
                    const deptApproved = deptBudgetRow ? (deptBudgetRow.capex + deptBudgetRow.opex) : 0;
                    const remaining = deptApproved - deptProjectedCost;
                    const outcomeTotal = outcome.totalProjectedCost || 0;
                    const pctOfThis = outcomeTotal > 0 ? (deptProjectedCost / outcomeTotal) * 100 : 0;
                    return (
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 2 }}>
                          {selectedDept}:
                        </Typography>
                        <Box component="span" sx={{ display: 'inline-flex', gap: 3 }}>
                          <span>Projected <strong>{formatCurrency(deptProjectedCost)}</strong></span>
                          {deptBudgetRow && <span>Approved <strong>{formatCurrency(deptApproved)}</strong></span>}
                          {deptBudgetRow && (
                            <Typography component="span" variant="caption"
                              sx={{ color: remaining >= 0 ? 'success.main' : 'error.main' }}>
                              Remaining <strong>{formatCurrency(Math.abs(remaining))}{remaining < 0 ? ' over' : ''}</strong>
                            </Typography>
                          )}
                          <span>% of outcome <strong>{pctOfThis.toFixed(1)}%</strong></span>
                        </Box>
                      </Paper>
                    );
                  })()}

                  {/* Unmatched project detail table for synthetic _unmatched outcome */}
                  {outcome.isSynthetic && (outcome.unmatchedSpendDetails?.length > 0 || outcome.unmatchedForecastDetails?.length > 0) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom color="warning.main">
                        Unmatched Project Codes
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        These project codes have no matching outcome. The company breakdown in the rows above already includes this spend — this table is for reference only.
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Project Code</TableCell>
                              <TableCell>Project Name</TableCell>
                              <TableCell>Company</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell align="right">Total</TableCell>
                              <TableCell align="right">Capex</TableCell>
                              <TableCell align="right">Opex</TableCell>
                              {MONTHS.map(m => (
                                <TableCell key={m} align="right">{m}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {outcome.unmatchedSpendDetails?.map(d => (
                              <TableRow key={`spend-${d.project_code}`}>
                                <TableCell><Typography variant="body2">{d.project_code}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color={d.project_name ? 'text.primary' : 'text.disabled'}>{d.project_name || '—'}</Typography></TableCell>
                                <TableCell>{d.company ? <Chip size="small" label={d.company} variant="outlined" /> : <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                                <TableCell><Chip size="small" label="Actual" color="warning" variant="outlined" /></TableCell>
                                <TableCell align="right"><Tooltip title={formatCurrencyFull(d.total)} placement="top" disableHoverListener={Math.abs(d.total) < 1_000_000}><span>{formatCurrency(d.total)}</span></Tooltip></TableCell>
                                <TableCell align="right"><Tooltip title={formatCurrencyFull(d.capex || 0)} placement="top" disableHoverListener={Math.abs(d.capex || 0) < 1_000_000}><span>{formatCurrency(d.capex || 0)}</span></Tooltip></TableCell>
                                <TableCell align="right"><Tooltip title={formatCurrencyFull(d.opex || 0)} placement="top" disableHoverListener={Math.abs(d.opex || 0) < 1_000_000}><span>{formatCurrency(d.opex || 0)}</span></Tooltip></TableCell>
                                {Array.from({ length: 12 }, (_, i) => {
                                  const amt = d.monthly?.[String(i + 1)] ?? null;
                                  return (
                                    <TableCell key={i} align="right">
                                      {amt !== null ? <Tooltip title={formatCurrencyFull(amt)} placement="top" disableHoverListener={Math.abs(amt) < 1_000_000}><span>{formatCurrency(amt)}</span></Tooltip> : '-'}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                            {outcome.unmatchedForecastDetails?.map(d => (
                              <TableRow key={`forecast-${d.project_code}`}>
                                <TableCell><Typography variant="body2">{d.project_code}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color={d.project_name ? 'text.primary' : 'text.disabled'}>{d.project_name || '—'}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color="text.disabled">—</Typography></TableCell>
                                <TableCell><Chip size="small" label="Forecast" color="secondary" variant="outlined" /></TableCell>
                                <TableCell align="right"><Tooltip title={formatCurrencyFull(d.total)} placement="top" disableHoverListener={Math.abs(d.total) < 1_000_000}><span>{formatCurrency(d.total)}</span></Tooltip></TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right"><Tooltip title={formatCurrencyFull(d.total)} placement="top" disableHoverListener={Math.abs(d.total) < 1_000_000}><span>{formatCurrency(d.total)}</span></Tooltip></TableCell>
                                {Array.from({ length: 12 }, (_, i) => {
                                  const amt = d.monthly?.[String(i + 1)] ?? null;
                                  return (
                                    <TableCell key={i} align="right">
                                      {amt !== null && amt > 0 ? <Tooltip title={formatCurrencyFull(amt)} placement="top" disableHoverListener={Math.abs(amt) < 1_000_000}><span>{formatCurrency(amt)}</span></Tooltip> : '-'}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                  {/* Monthly Breakdown + Company Breakdown — single table so Jan columns align */}
                  {(() => {
                    const allCompanies = new Set([
                      ...Object.keys(outcome.companyBreakdown || {}),
                      ...Object.keys(outcome.companyActuals || {}),
                      ...Object.keys(outcome.companyOfficialForecast || {})
                    ]);
                    const hasCompanyBreakdown = allCompanies.size > 0;
                    const companyRows = [];

                    // Build dept grouping: merge ungrouped companies (actuals/official only, no staffing role)
                    // into the Unassigned dept so everything lives under a dept header.
                    const rawDeptBreakdown = outcome.deptCompanyBreakdown || {};
                    const deptCompanyBreakdown = { ...rawDeptBreakdown };
                    const deptedCompanies = new Set(
                      Object.values(deptCompanyBreakdown).flatMap(co => Object.keys(co))
                    );
                    Array.from(allCompanies).filter(c => !deptedCompanies.has(c)).forEach(c => {
                      if (!deptCompanyBreakdown['Unassigned']) deptCompanyBreakdown['Unassigned'] = {};
                      // No dept-scoped projected slice — pass null so full-company breakdown is used
                      deptCompanyBreakdown['Unassigned'][c] = null;
                    });
                    const hasDepts = Object.keys(deptCompanyBreakdown).length > 0;
                    const subRowSxShared  = (color) => ({ bgcolor: `${color}.50` });
                    const subLabelSxShared = { pl: 3, fontSize: '0.75rem', color: 'text.secondary' };
                    const chipSzShared = { fontSize: '0.65rem', height: 18 };

                    // Helper: build all rows for one company, push into target array.
                    // Row order: Projected → Actual → Variance → Official Forecast → Staffing Forecast
                    // projOverride: when provided (dept context), projected row shows dept-scoped amount;
                    //   actual/variance/official/staffing always use full-company totals.
                    // Any row with zero total and all-zero monthly is suppressed.
                    const buildCompanyRows = (company, companyIdx, target, keyPrefix = '', projOverride = null) => {
                      const k = `${keyPrefix}${company}`;
                      // fullBreakdown = combined company total (official+fixed for CA, staffing for others)
                      const fullBreakdown = outcome.companyBreakdown?.[company] || { total: 0, capex: 0, opex: 0, monthlyForecast: [], monthlyCapex: [], monthlyOpex: [] };
                      // When in a dept context, projected row shows dept-scoped slice; actual/variance use full-company totals
                      const breakdown = projOverride
                        ? { ...fullBreakdown, total: projOverride.total, capex: projOverride.capex, opex: projOverride.opex, monthlyForecast: projOverride.monthlyForecast, monthlyCapex: null, monthlyOpex: null }
                        : fullBreakdown;
                      const actual    = outcome.companyActuals?.[company]   || { total: 0, capex: 0, opex: 0 };
                      const official  = outcome.companyOfficialForecast?.[company] || { total: 0 };
                      // Pre-mutation CA staffing snapshot (only present for CA when official exists)
                      const staffing  = outcome.companyStaffingBreakdown?.[company] || null;
                      const monthlyActual    = outcome.companyMonthlyActuals?.[company] || Array(12).fill(0);
                      const monthlyProjected = breakdown.monthlyForecast || Array(12).fill(0);
                      // Variance baseline: always full-company projected (actual spend has no dept attribution)
                      const fullMonthlyProjected = fullBreakdown.monthlyForecast || Array(12).fill(0);
                      const monthlyOfficial  = official.monthlyForecast   || Array(12).fill(0);
                      const isUnmapped = outcome.companyActuals?.[company]?.unmapped === true ||
                        outcome.companyOfficialForecast?.[company]?.unmapped === true;
                      const vendorIds = actual.vendorIds || [];
                      const vendorIdTooltip = vendorIds.length > 0 ? `Vendor ID: ${vendorIds.join(', ')}` : '';
                      const rowBorder = { borderTop: '2px solid', borderTopColor: 'divider' };
                      const subRowSx  = subRowSxShared;
                      const subLabelSx = subLabelSxShared;
                      const chipSz = chipSzShared;

                      const prorateMonthly = (monthly, portion, tot) =>
                        tot > 0 ? monthly.map(v => (v || 0) * portion / tot) : Array(12).fill(0);

                      // Determine last month with actual data (for variance total scoping)
                      const lastActualIdx = (() => {
                        let last = -1;
                        for (let i = 0; i < 12; i++) if ((monthlyActual[i] || 0) > 0) last = i;
                        return last;
                      })();

                      const companyLabel = (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Tooltip title={vendorIdTooltip} disableHoverListener={!vendorIdTooltip} arrow>
                            <Typography variant="body2" fontWeight={600}>{company}</Typography>
                          </Tooltip>
                          {isUnmapped && <Chip size="small" label="Unmatched" color="warning" variant="outlined" sx={chipSz} />}
                        </Box>
                      );

                      const pushRow = (rowKey, badgeChip, total, monthly, rowSx = {}, isFirst = false, hasCapexRows = false, textColor = null) => {
                        if (total === 0 && !monthly.some(v => (v || 0) !== 0)) return; // hide zero rows
                        const effectiveSx = { ...rowSx, ...(isFirst ? { ...rowBorder, bgcolor: 'action.hover' } : {}) };
                        target.push(
                          <TableRow key={rowKey} sx={effectiveSx}>
                            <TableCell sx={{ fontSize: '0.8rem', position: 'sticky', left: 0, zIndex: 1, bgcolor: isFirst ? 'action.hover' : 'background.paper' }}>
                              <Box display="flex" alignItems="center" width="100%" gap={1}>
                                {isFirst && companyLabel}
                                {badgeChip}
                                {hasCapexRows && (
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleCapex(rowKey); }}
                                    sx={{ ml: 'auto', p: 0.25, flexShrink: 0 }}>
                                    {expandedCapex.has(rowKey) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', ...(textColor ? { color: textColor } : {}) }}>
                              <Tooltip title={formatCurrencyFull(total)} placement="left" disableHoverListener={Math.abs(total) < 1_000_000}>
                                <span>{formatCurrency(total)}</span>
                              </Tooltip>
                            </TableCell>
                            {MONTHS.map((_, i) => (
                              <TableCell key={i} align="right" sx={{ fontSize: '0.75rem', ...(textColor ? { color: textColor } : {}) }}>
                                {(monthly[i] || 0) !== 0
                                  ? <Tooltip title={formatCurrencyFull(monthly[i])} placement="top" disableHoverListener={Math.abs(monthly[i] || 0) < 1_000_000}><span>{formatCurrency(monthly[i])}</span></Tooltip>
                                  : '—'}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      };

                      const pushSubRows = (rowKey, color, capexVal, opexVal, capexMonthly, opexMonthly) => {
                        if (capexVal === 0 && opexVal === 0) return;
                        if (!expandedCapex.has(rowKey)) return; // collapsed by default
                        if (capexVal !== 0) target.push(
                          <TableRow key={`${rowKey}-CAPEX`} sx={subRowSx(color)}>
                            <TableCell sx={{ ...subLabelSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${color}.50` }}><Chip label="CAPEX" size="small" color={color} variant="outlined" sx={chipSz} /></TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(capexVal)} placement="left" disableHoverListener={Math.abs(capexVal) < 1_000_000}><span>{formatCurrency(capexVal)}</span></Tooltip></TableCell>
                            {MONTHS.map((_, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{(capexMonthly[i] || 0) !== 0 ? <Tooltip title={formatCurrencyFull(capexMonthly[i])} placement="top" disableHoverListener={Math.abs(capexMonthly[i] || 0) < 1_000_000}><span>{formatCurrency(capexMonthly[i])}</span></Tooltip> : '—'}</TableCell>)}
                          </TableRow>
                        );
                        if (opexVal !== 0) target.push(
                          <TableRow key={`${rowKey}-OPEX`} sx={subRowSx(color)}>
                            <TableCell sx={{ ...subLabelSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${color}.50` }}><Chip label="OPEX" size="small" color={color} variant="outlined" sx={chipSz} /></TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(opexVal)} placement="left" disableHoverListener={Math.abs(opexVal) < 1_000_000}><span>{formatCurrency(opexVal)}</span></Tooltip></TableCell>
                            {MONTHS.map((_, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{(opexMonthly[i] || 0) !== 0 ? <Tooltip title={formatCurrencyFull(opexMonthly[i])} placement="top" disableHoverListener={Math.abs(opexMonthly[i] || 0) < 1_000_000}><span>{formatCurrency(opexMonthly[i])}</span></Tooltip> : '—'}</TableCell>)}
                          </TableRow>
                        );
                      };

                      const isCAWithOfficial = company === 'CA' && outcome.usedOfficialForecast;
                      // When showing a dept-scoped projected row, label it "Projected (Dept)" so it's clear
                      // it represents only this department's share, not the full company total.
                      const projLabel = isCAWithOfficial
                        ? (projOverride ? 'Official + Proj (Dept)' : 'Official + Projected')
                        : (projOverride ? 'Projected (Dept)' : 'Projected');
                      const projColor = 'success';
                      const projCapexMonthly = breakdown.monthlyCapex?.length
                        ? breakdown.monthlyCapex
                        : prorateMonthly(monthlyProjected, breakdown.capex, breakdown.capex + breakdown.opex);
                      const projOpexMonthly = breakdown.monthlyOpex?.length
                        ? breakdown.monthlyOpex
                        : prorateMonthly(monthlyProjected, breakdown.opex, breakdown.capex + breakdown.opex);

                      // 1. Projected row — dept-scoped when projOverride provided, full company otherwise
                      const deptLabel = keyPrefix ? keyPrefix.replace(/-$/, '') : '';
                      const projTooltip = isCAWithOfficial
                        ? (projOverride
                            ? `Official Forecast (Finance), ${deptLabel || 'this'} allocation only.`
                            : 'Official Forecast (Finance) + fixed costs for CA.')
                        : (projOverride
                            ? `PPM Staffing Model, ${deptLabel || 'this'} allocation only.`
                            : `PPM Staffing Model projection for ${company}.`);
                      pushRow(`${k}-proj`,
                        <Tooltip arrow title={projTooltip}><Chip label={projLabel} size="small" color={projColor} variant="outlined" sx={chipSz} /></Tooltip>,
                        breakdown.total, monthlyProjected, {}, true,
                        breakdown.capex !== 0 || breakdown.opex !== 0, 'success.main');

                      // Build filtered fixed costs for this company/dept context once
                      const visibleFixedCosts = Object.entries(outcome.fixedCostBreakdown || {}).filter(([, fc]) => {
                        if ((fc.company || 'Other') !== company) return false;
                        const fcDepts = fc.departments || [];
                        const currentDept = keyPrefix ? keyPrefix.replace(/-$/, '') : null;
                        if (currentDept) {
                          if (fcDepts.length > 0) return fcDepts.includes(currentDept);
                          const firstDept = Object.keys(deptCompanyBreakdown).find(d =>
                            Object.prototype.hasOwnProperty.call(deptCompanyBreakdown[d], company)
                          );
                          return firstDept === currentDept;
                        }
                        return true;
                      });

                      // Projected CAPEX/OPEX sub-rows, with fixed cost children nested inside each
                      if ((breakdown.capex !== 0 || breakdown.opex !== 0) && expandedCapex.has(`${k}-proj`)) {
                        if (breakdown.capex !== 0) {
                          target.push(
                            <TableRow key={`${k}-proj-CAPEX`} sx={subRowSx(projColor)}>
                              <TableCell sx={{ ...subLabelSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${projColor}.50` }}><Chip label="CAPEX" size="small" color={projColor} variant="outlined" sx={chipSz} /></TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(breakdown.capex)} placement="left" disableHoverListener={Math.abs(breakdown.capex) < 1_000_000}><span>{formatCurrency(breakdown.capex)}</span></Tooltip></TableCell>
                              {MONTHS.map((_, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{(projCapexMonthly[i] || 0) !== 0 ? <Tooltip title={formatCurrencyFull(projCapexMonthly[i])} placement="top" disableHoverListener={Math.abs(projCapexMonthly[i] || 0) < 1_000_000}><span>{formatCurrency(projCapexMonthly[i])}</span></Tooltip> : '—'}</TableCell>)}
                            </TableRow>
                          );
                          visibleFixedCosts.forEach(([costName, fc]) => {
                            if ((fc.capex || 0) === 0) return;
                            const fcMonthly = fc.monthlyCapex || Array(12).fill(0);
                            target.push(
                              <TableRow key={`${k}-proj-CAPEX-fc-${costName}`} sx={subRowSx(projColor)}>
                                <TableCell sx={{ ...subLabelSx, pl: 8, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${projColor}.50` }}>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Chip label="Fixed Cost" size="small" color={projColor} variant="outlined" sx={chipSz} />
                                    <Tooltip arrow title={fc.details || ''} disableHoverListener={!fc.details}>
                                      <Typography variant="caption">{costName}</Typography>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(fc.capex)} placement="left" disableHoverListener={Math.abs(fc.capex) < 1_000_000}><span>{formatCurrency(fc.capex)}</span></Tooltip></TableCell>
                                {fcMonthly.map((v, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{(v || 0) !== 0 ? <Tooltip title={formatCurrencyFull(v)} placement="top" disableHoverListener={Math.abs(v || 0) < 1_000_000}><span>{formatCurrency(v)}</span></Tooltip> : '—'}</TableCell>)}
                              </TableRow>
                            );
                          });
                        }
                        if (breakdown.opex !== 0) {
                          target.push(
                            <TableRow key={`${k}-proj-OPEX`} sx={subRowSx(projColor)}>
                              <TableCell sx={{ ...subLabelSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${projColor}.50` }}><Chip label="OPEX" size="small" color={projColor} variant="outlined" sx={chipSz} /></TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(breakdown.opex)} placement="left" disableHoverListener={Math.abs(breakdown.opex) < 1_000_000}><span>{formatCurrency(breakdown.opex)}</span></Tooltip></TableCell>
                              {MONTHS.map((_, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{(projOpexMonthly[i] || 0) !== 0 ? <Tooltip title={formatCurrencyFull(projOpexMonthly[i])} placement="top" disableHoverListener={Math.abs(projOpexMonthly[i] || 0) < 1_000_000}><span>{formatCurrency(projOpexMonthly[i])}</span></Tooltip> : '—'}</TableCell>)}
                            </TableRow>
                          );
                          visibleFixedCosts.forEach(([costName, fc]) => {
                            if ((fc.opex || 0) === 0) return;
                            const fcMonthly = fc.monthlyOpex || Array(12).fill(0);
                            target.push(
                              <TableRow key={`${k}-proj-OPEX-fc-${costName}`} sx={subRowSx(projColor)}>
                                <TableCell sx={{ ...subLabelSx, pl: 8, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${projColor}.50` }}>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Chip label="Fixed Cost" size="small" color={projColor} variant="outlined" sx={chipSz} />
                                    <Tooltip arrow title={fc.details || ''} disableHoverListener={!fc.details}>
                                      <Typography variant="caption">{costName}</Typography>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(fc.opex)} placement="left" disableHoverListener={Math.abs(fc.opex) < 1_000_000}><span>{formatCurrency(fc.opex)}</span></Tooltip></TableCell>
                                {fcMonthly.map((v, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{(v || 0) !== 0 ? <Tooltip title={formatCurrencyFull(v)} placement="top" disableHoverListener={Math.abs(v || 0) < 1_000_000}><span>{formatCurrency(v)}</span></Tooltip> : '—'}</TableCell>)}
                              </TableRow>
                            );
                          });
                        }
                      }

                      // 2. Actual — always full-company total (no dept attribution in actuals)
                      const actualMonthlyCapex = actual.monthlyCapex || prorateMonthly(monthlyActual, actual.capex, actual.total);
                      const actualMonthlyOpex  = actual.monthlyOpex  || prorateMonthly(monthlyActual, actual.opex,  actual.total);
                      pushRow(`${k}-actual`,
                        <Tooltip arrow title={`Invoiced spend for ${company} from uploaded actuals.`}><Chip label="Actual" size="small" color="warning" variant="outlined" sx={chipSz} /></Tooltip>,
                        actual.total, monthlyActual, {}, false,
                        actual.capex !== 0 || actual.opex !== 0, 'warning.main');
                      pushSubRows(`${k}-actual`, 'warning', actual.capex, actual.opex, actualMonthlyCapex, actualMonthlyOpex);

                      // 3. Variance — actual vs full-company projected; only months with actual data
                      if (actual.total > 0 && lastActualIdx >= 0) {
                        // Use full-company projected as baseline — dept slice would make variance meaningless
                        const fullProjCapexMonthly = fullBreakdown.monthlyCapex?.length
                          ? fullBreakdown.monthlyCapex
                          : prorateMonthly(fullMonthlyProjected, fullBreakdown.capex, fullBreakdown.capex + fullBreakdown.opex);
                        const fullProjOpexMonthly = fullBreakdown.monthlyOpex?.length
                          ? fullBreakdown.monthlyOpex
                          : prorateMonthly(fullMonthlyProjected, fullBreakdown.opex, fullBreakdown.capex + fullBreakdown.opex);
                        const varMonthly = MONTHS.map((_, i) => {
                          const a = monthlyActual[i] || 0;
                          return i <= lastActualIdx ? a - (fullMonthlyProjected[i] || 0) : null;
                        });
                        const varTotal = varMonthly.slice(0, lastActualIdx + 1).reduce((s, v) => s + (v || 0), 0);
                        const varCapexMonthly = MONTHS.map((_, i) =>
                          i <= lastActualIdx ? (actualMonthlyCapex[i] || 0) - (fullProjCapexMonthly[i] || 0) : null);
                        const varOpexMonthly  = MONTHS.map((_, i) =>
                          i <= lastActualIdx ? (actualMonthlyOpex[i]  || 0) - (fullProjOpexMonthly[i]  || 0) : null);
                        const varCapexTotal = varCapexMonthly.slice(0, lastActualIdx + 1).reduce((s, v) => s + (v || 0), 0);
                        const varOpexTotal  = varOpexMonthly.slice(0, lastActualIdx + 1).reduce((s, v) => s + (v || 0), 0);
                        const varColor = varTotal >= 0 ? 'error' : 'success';
                        const hasVarCapex = varCapexTotal !== 0 || varOpexTotal !== 0;
                        target.push(
                          <TableRow key={`${k}-variance`} sx={subRowSx(varColor)}>
                            <TableCell sx={{ ...subLabelSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${varColor}.50` }}>
                              <Box display="flex" alignItems="center" width="100%" gap={1}>
                                <Tooltip arrow title={`Actual vs projected for ${company}. Red = over, green = under.`}><Chip label="Variance" size="small" color={varColor} variant="outlined" sx={chipSz} /></Tooltip>
                                {hasVarCapex && (
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleCapex(`${k}-variance`); }}
                                    sx={{ ml: 'auto', p: 0.25, flexShrink: 0 }}>
                                    {expandedCapex.has(`${k}-variance`) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                              <Tooltip title={formatCurrencyFull(varTotal)} placement="left" disableHoverListener={Math.abs(varTotal) < 1_000_000}>
                                <Typography variant="caption" color={`${varColor}.main`} fontWeight={600}>
                                  {varTotal >= 0 ? '+' : ''}{formatCurrency(varTotal)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            {varMonthly.map((v, i) => (
                              <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>
                                {v !== null ? <Tooltip title={formatCurrencyFull(v)} placement="top" disableHoverListener={Math.abs(v) < 1_000_000}><Typography variant="caption" color={v >= 0 ? 'error.main' : 'success.main'}>{v >= 0 ? '+' : ''}{formatCurrency(v)}</Typography></Tooltip> : '—'}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                        if (expandedCapex.has(`${k}-variance`)) {
                          if (varCapexTotal !== 0) target.push(
                            <TableRow key={`${k}-variance-CAPEX`} sx={subRowSx(varColor)}>
                              <TableCell sx={{ ...subLabelSx, pl: 5, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${varColor}.50` }}><Chip label="CAPEX" size="small" color={varColor} variant="outlined" sx={chipSz} /></TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(varCapexTotal)} placement="left" disableHoverListener={Math.abs(varCapexTotal) < 1_000_000}><Typography variant="caption" color={`${varColor}.main`}>{varCapexTotal >= 0 ? '+' : ''}{formatCurrency(varCapexTotal)}</Typography></Tooltip></TableCell>
                              {varCapexMonthly.map((v, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{v !== null ? <Tooltip title={formatCurrencyFull(v)} placement="top" disableHoverListener={Math.abs(v) < 1_000_000}><Typography variant="caption" color={v >= 0 ? 'error.main' : 'success.main'}>{v >= 0 ? '+' : ''}{formatCurrency(v)}</Typography></Tooltip> : '—'}</TableCell>)}
                            </TableRow>
                          );
                          if (varOpexTotal !== 0) target.push(
                            <TableRow key={`${k}-variance-OPEX`} sx={subRowSx(varColor)}>
                              <TableCell sx={{ ...subLabelSx, pl: 5, position: 'sticky', left: 0, zIndex: 1, bgcolor: `${varColor}.50` }}><Chip label="OPEX" size="small" color={varColor} variant="outlined" sx={chipSz} /></TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}><Tooltip title={formatCurrencyFull(varOpexTotal)} placement="left" disableHoverListener={Math.abs(varOpexTotal) < 1_000_000}><Typography variant="caption" color={`${varColor}.main`}>{varOpexTotal >= 0 ? '+' : ''}{formatCurrency(varOpexTotal)}</Typography></Tooltip></TableCell>
                              {varOpexMonthly.map((v, i) => <TableCell key={i} align="right" sx={{ fontSize: '0.75rem' }}>{v !== null ? <Tooltip title={formatCurrencyFull(v)} placement="top" disableHoverListener={Math.abs(v) < 1_000_000}><Typography variant="caption" color={v >= 0 ? 'error.main' : 'success.main'}>{v >= 0 ? '+' : ''}{formatCurrency(v)}</Typography></Tooltip> : '—'}</TableCell>)}
                            </TableRow>
                          );
                        }
                      }

                      // 4. Official Forecast (standalone, only if present)
                      if (official.total > 0) {
                        const offCapexMonthly = prorateMonthly(monthlyOfficial, official.capex, official.capex + official.opex);
                        const offOpexMonthly  = prorateMonthly(monthlyOfficial, official.opex,  official.capex + official.opex);
                        pushRow(`${k}-official`,
                          <Tooltip arrow title={`Official Forecast from Finance for ${company}.`}><Chip label="Official Forecast" size="small" color="secondary" variant="outlined" sx={chipSz} /></Tooltip>,
                          official.total, monthlyOfficial, {}, false,
                          official.capex !== 0 || official.opex !== 0, 'secondary.main');
                        pushSubRows(`${k}-official`, 'secondary', official.capex, official.opex, offCapexMonthly, offOpexMonthly);
                      }

                      // 5. Staffing Forecast (only for CA when official exists)
                      if (isCAWithOfficial && staffing && staffing.total > 0) {
                        const staffMonthly = staffing.monthlyForecast || Array(12).fill(0);
                        const staffCapexMonthly = prorateMonthly(staffMonthly, staffing.capex, staffing.capex + staffing.opex);
                        const staffOpexMonthly  = prorateMonthly(staffMonthly, staffing.opex,  staffing.capex + staffing.opex);
                        pushRow(`${k}-staffing`,
                          <Tooltip arrow title="PPM Staffing Model projection for CA."><Chip label="Staffing Forecast" size="small" color="success" variant="outlined" sx={chipSz} /></Tooltip>,
                          staffing.total, staffMonthly, {}, false,
                          staffing.capex !== 0 || staffing.opex !== 0, 'success.main');
                        pushSubRows(`${k}-staffing`, 'success', staffing.capex, staffing.opex, staffCapexMonthly, staffOpexMonthly);
                      }

                    }; // end buildCompanyRows

                    if (hasDepts) {
                      // Dept-grouped company rows
                      Object.entries(deptCompanyBreakdown).forEach(([dept, deptCos], deptIdx) => {
                        const deptKey = `${outcome.id}-dept-${dept}`;
                        const isExpanded = expandedDepts[deptKey] !== false; // default open
                        // Dept-scoped projected total: sum of each company's dept slice
                        const deptMonthlyProj = Array(12).fill(0);
                        Object.values(deptCos).forEach(projOverride => {
                          if (!projOverride) return;
                          (projOverride.monthlyForecast || []).forEach((v, i) => { deptMonthlyProj[i] += v || 0; });
                        });
                        const deptTotalProj = deptMonthlyProj.reduce((s, v) => s + v, 0);

                        // Actual spend is full-company (no dept attribution) — best available approximation
                        const deptTotalActual = Object.keys(deptCos).reduce((sum, co) =>
                          sum + (outcome.companyActuals?.[co]?.total || 0), 0);
                        const deptVariance = deptTotalActual > 0 ? deptTotalActual - deptTotalProj : null;

                        // Dept header row
                        companyRows.push(
                          <TableRow
                            key={`dept-header-${dept}`}
                            onClick={() => setExpandedDepts(prev => ({ ...prev, [deptKey]: !isExpanded }))}
                            sx={{ cursor: 'pointer', borderTop: '3px solid', borderTopColor: deptIdx === 0 ? 'primary.main' : 'divider', backgroundColor: 'action.selected', '&:hover': { backgroundColor: 'action.focus' } }}
                          >
                            <TableCell>
                              <Tooltip arrow placement="right" title="Projected is dept-scoped. Actual variance uses full-company spend across these vendors.">
                              <Box display="flex" alignItems="center" gap={1}>
                                <IconButton size="small" sx={{ p: 0.25 }}>
                                  {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                                <Typography variant="body2" fontWeight="bold">{dept}</Typography>
                                <Chip label="Dept" size="small" color="default" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                              </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Box display="flex" flexDirection="column" alignItems="flex-end">
                                <Tooltip title={formatCurrencyFull(deptTotalProj)} placement="left" disableHoverListener={Math.abs(deptTotalProj) < 1_000_000}>
                                  <Typography variant="body2" fontWeight="bold">{formatCurrency(deptTotalProj)}</Typography>
                                </Tooltip>
                                {deptVariance !== null && (
                                  <Tooltip title={formatCurrencyFull(deptVariance)} placement="left" disableHoverListener={Math.abs(deptVariance) < 1_000_000}>
                                    <Typography variant="caption" color={deptVariance >= 0 ? 'error.main' : 'success.main'}>
                                      {deptVariance >= 0 ? '+' : ''}{formatCurrency(deptVariance)} actual
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            {MONTHS.map((_, i) => (
                              <TableCell key={i} align="right" sx={{ fontSize: '0.75rem', color: deptMonthlyProj[i] ? 'text.primary' : 'text.disabled' }}>
                                {deptMonthlyProj[i]
                                  ? <Tooltip title={formatCurrencyFull(deptMonthlyProj[i])} placement="top" disableHoverListener={Math.abs(deptMonthlyProj[i]) < 1_000_000}><span>{formatCurrency(deptMonthlyProj[i])}</span></Tooltip>
                                  : '—'}
                              </TableCell>
                            ))}
                          </TableRow>
                        );

                        // Company rows within this dept (only when expanded)
                        // projOverride gives dept-scoped projected amount; actual/variance/official/staffing
                        // use full-company totals since actuals have no dept attribution.
                        if (isExpanded) {
                          Object.keys(deptCos).forEach((company, coIdx) => {
                            buildCompanyRows(company, coIdx, companyRows, `${dept}-`, deptCompanyBreakdown[dept][company]);
                          });
                        }
                      });

                    } else {
                      // No dept data — flat company list (original behaviour)
                      Array.from(allCompanies).forEach((company, companyIdx) => {
                        buildCompanyRows(company, companyIdx, companyRows);
                      });
                    }

                    return (
                      <TableContainer component={Paper} sx={{ mb: 3, overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }}>Type</TableCell>
                              <TableCell align="right">Total</TableCell>
                              {MONTHS.map((month) => (
                                <TableCell key={month} align="right">{month}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Row 0: Approved Budget — only when budget exists */}
                            {(() => {
                              const budgetTotal = (outcome.approved_budget?.capex || 0) + (outcome.approved_budget?.opex || 0);
                              if (budgetTotal === 0) return null;
                              const budgetMonthly = outcome.monthlyBudget || Array(12).fill(null);
                              return (
                                <TableRow>
                                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                                    <Tooltip arrow placement="right" title="Approved budget for this outcome (CAPEX + OPEX).">
                                    <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'help', width: 'fit-content' }}>
                                      <Typography variant="body2" fontWeight="bold" color="primary.main">Approved Budget</Typography>
                                      <Chip size="small" label="Budget" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title={formatCurrencyFull(budgetTotal)} placement="left" disableHoverListener={Math.abs(budgetTotal) < 1_000_000}>
                                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                                        {formatCurrency(budgetTotal)}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  {budgetMonthly.map((amount, index) => (
                                    <TableCell key={index} align="right">
                                      {amount !== null
                                        ? <Tooltip title={formatCurrencyFull(amount)} placement="top" disableHoverListener={Math.abs(amount) < 1_000_000}><span>{formatCurrency(amount)}</span></Tooltip>
                                        : '—'}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })()}

                            {/* EOY Projection row — only when actuals exist */}
                            {(() => {
                              const cutoff = outcome.latestActualSpendMonth ?? -1;
                              if (cutoff < 0) return null;
                              const monthlyActual = outcome.monthlyActual || [];
                              const eoyMonthly = effectiveMonthlyProjected.map((proj, i) =>
                                i <= cutoff ? (monthlyActual[i] || 0) : proj
                              );
                              const eoyTotal = eoyMonthly.reduce((s, v) => s + (v || 0), 0);
                              return (
                                <TableRow sx={{ backgroundColor: alpha('#0288d1', 0.06) }}>
                                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: alpha('#0288d1', 0.06) }}>
                                    <Tooltip arrow placement="right" title="Full-year landing estimate: actuals through the latest uploaded month, projected for remaining months.">
                                      <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'help', width: 'fit-content' }}>
                                        <Typography variant="body2" fontWeight="bold" color="info.main">EOY Projection</Typography>
                                        <Chip size="small" label="EOY" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                      </Box>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title={formatCurrencyFull(eoyTotal)} placement="top" disableHoverListener={Math.abs(eoyTotal) < 1_000_000}>
                                      <Typography variant="body2" fontWeight="bold" color="info.main">
                                        {formatCurrency(eoyTotal)}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  {eoyMonthly.map((amount, index) => (
                                    <TableCell key={index} align="right" sx={{ fontSize: '0.75rem', color: 'info.main' }}>
                                      {amount > 0
                                        ? <Tooltip title={formatCurrencyFull(amount)} placement="top" disableHoverListener={Math.abs(amount) < 1_000_000}>
                                            <span>{formatCurrency(amount)}</span>
                                          </Tooltip>
                                        : '—'}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })()}

                            {/* Row 1: Official + Projected Forecast (combined) — hidden for synthetic _unmatched outcomes */}
                            {!outcome.isSynthetic && (
                              <TableRow>
                                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                                  <Tooltip arrow placement="right" title={outcome.usedOfficialForecast
                                    ? 'CA uses the Official Forecast from Finance; all other vendors use the PPM Staffing Model. Fixed costs included.'
                                    : outcome.usingStaffingData
                                      ? 'PPM Staffing Model projection, including fixed costs.'
                                      : 'No forecast data available for this outcome.'}>
                                  <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'help', width: 'fit-content' }}>
                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                      {outcome.usedOfficialForecast ? 'Official + Projected Forecast' : 'Projected Forecast'}
                                    </Typography>
                                    {outcome.usedOfficialForecast ? (
                                      <Chip size="small" label="CA: Official · Others: Staffing" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                    ) : outcome.usingStaffingData ? (
                                      <Chip size="small" label="Staffing" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                    ) : (
                                      <Chip size="small" label="No Data" color="default" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                    )}
                                  </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title={formatCurrencyFull(effectiveTotalProjectedCost)} placement="left" disableHoverListener={Math.abs(effectiveTotalProjectedCost) < 1_000_000}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {formatCurrency(effectiveTotalProjectedCost)}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                {effectiveMonthlyProjected.map((amount, index) => (
                                  <TableCell key={index} align="right">
                                    <Tooltip title={formatCurrencyFull(amount)} placement="top" disableHoverListener={Math.abs(amount || 0) < 1_000_000}><span>{formatCurrency(amount)}</span></Tooltip>
                                  </TableCell>
                                ))}
                              </TableRow>
                            )}

                            {/* Row 2: Projected Forecast (Staffing only) — only shown when official is present */}
                            {!outcome.isSynthetic && outcome.usedOfficialForecast && (
                              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell sx={{ pl: 3, position: 'sticky', left: 0, zIndex: 1, bgcolor: 'action.hover' }}>
                                  <Tooltip arrow placement="right" title="PPM Staffing Model projection for all vendors including CA.">
                                  <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'help', width: 'fit-content' }}>
                                    <Typography variant="body2" color="success.main">
                                      Projected Forecast (Staffing)
                                    </Typography>
                                    <Chip size="small" label="Staffing Model" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                  </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title={formatCurrencyFull(outcome.totalStaffingCost || 0)} placement="left" disableHoverListener={Math.abs(outcome.totalStaffingCost || 0) < 1_000_000}>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatCurrency(outcome.totalStaffingCost || 0)}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                {(outcome.monthlyStaffingProjected || Array(12).fill(0)).map((amount, index) => (
                                  <TableCell key={index} align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {amount > 0 ? <Tooltip title={formatCurrencyFull(amount)} placement="top" disableHoverListener={Math.abs(amount) < 1_000_000}><span>{formatCurrency(amount)}</span></Tooltip> : '—'}
                                  </TableCell>
                                ))}
                              </TableRow>
                            )}

                            {/* Row 3: Official Forecast (if available) */}
                            {outcome.hasOfficialForecast && (
                              <TableRow>
                                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                                  <Tooltip arrow placement="right" title="Official Forecast from Finance — CA staffing costs only.">
                                  <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'help', width: 'fit-content' }}>
                                    <Typography variant="body2" fontWeight="bold" color="secondary.main">
                                      Official Forecast
                                    </Typography>
                                    <Chip size="small" label="Uploaded" color="secondary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                  </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title={formatCurrencyFull(outcome.totalOfficialForecast)} placement="left" disableHoverListener={Math.abs(outcome.totalOfficialForecast) < 1_000_000}>
                                    <Typography variant="body2" fontWeight="bold" color="secondary.main">
                                      {formatCurrency(outcome.totalOfficialForecast)}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                {outcome.monthlyOfficialForecast.map((amount, index) => (
                                  <TableCell key={index} align="right">
                                    {amount !== null
                                      ? <Tooltip title={formatCurrencyFull(amount)} placement="top" disableHoverListener={Math.abs(amount) < 1_000_000}><span>{formatCurrency(amount)}</span></Tooltip>
                                      : '-'}
                                  </TableCell>
                                ))}
                              </TableRow>
                            )}

                            {/* Row 4: Actual Spend */}
                            <TableRow>
                              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                                <Tooltip arrow placement="right" title="Invoiced costs uploaded from Finance through the latest available period.">
                                <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'help', width: 'fit-content' }}>
                                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                                    Actual Spend
                                  </Typography>
                                  {outcome.hasUploadedSpendData && (
                                    <Chip size="small" label="Uploaded" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                  )}
                                  {outcome.latestActualSpendMonthName && (
                                    <Chip size="small" label={`Through ${outcome.latestActualSpendMonthName}`} color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                  )}
                                </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={formatCurrencyFull(outcome.totalActualSpend)} placement="left" disableHoverListener={Math.abs(outcome.totalActualSpend) < 1_000_000}>
                                  <Typography variant="body2" fontWeight="bold" color="success.main">
                                    {formatCurrency(outcome.totalActualSpend)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              {outcome.monthlyActual.map((amount, index) => (
                                <TableCell key={index} align="right">
                                  {amount !== null
                                    ? <Tooltip title={formatCurrencyFull(amount)} placement="top" disableHoverListener={Math.abs(amount) < 1_000_000}><span>{formatCurrency(amount)}</span></Tooltip>
                                    : '-'}
                                </TableCell>
                              ))}
                            </TableRow>

                            {/* Row 5: Variance (Actual vs combined forecast) */}
                            {(() => {
                              const varianceBaseline = effectiveTotalProjectedCost;
                              const monthlyBaseline = effectiveMonthlyProjected;
                              const varianceLabel = outcome.usedOfficialForecast
                                ? 'Variance (Actual vs Official + Projected)'
                                : 'Variance (Actual vs Projected)';
                              const varianceTotal = outcome.totalActualSpend - varianceBaseline;
                              return (
                                <TableRow>
                                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                                    <Tooltip arrow placement="right" title="Actual vs forecast. Red = over budget, green = under. Only months with actuals included.">
                                    <Box sx={{ cursor: 'help', width: 'fit-content' }}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {varianceLabel}
                                    </Typography>
                                    </Box>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title={formatCurrencyFull(outcome.totalActualSpend - varianceBaseline)} placement="left" disableHoverListener={Math.abs(outcome.totalActualSpend - varianceBaseline) < 1_000_000}>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color={(outcome.totalActualSpend - varianceBaseline) >= 0 ? 'error.main' : 'success.main'}
                                      >
                                        {formatCurrency(outcome.totalActualSpend - varianceBaseline)}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  {outcome.monthlyActual.map((actual, index) => {
                                    const baseline = monthlyBaseline[index];
                                    const variance = actual !== null && baseline !== null ? actual - baseline : null;
                                    return (
                                      <TableCell key={index} align="right">
                                        {variance !== null ? (
                                          <Tooltip title={formatCurrencyFull(variance)} placement="top" disableHoverListener={Math.abs(variance) < 1_000_000}>
                                            <Typography
                                              variant="body2"
                                              color={variance >= 0 ? 'error.main' : 'success.main'}
                                            >
                                              {formatCurrency(variance)}
                                            </Typography>
                                          </Tooltip>
                                        ) : '-'}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })()}

                            {/* Company / Dept Breakdown rows — same table so month columns align */}
                            {hasCompanyBreakdown && (
                              <TableRow>
                                <TableCell colSpan={2 + MONTHS.length} sx={{ pt: 2, pb: 0.5, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                                  <Typography variant="subtitle2" color="primary">
                                    {hasDepts ? 'Department & Company Breakdown' : 'Company Breakdown'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            {companyRows}

                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {!loading && visibleOutcomes.length === 0 && (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No outcomes found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlanId ? 
                  'The selected plan has no approved or complete outcomes.' :
                  'No approved or complete outcomes available.'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

            {/* Spend Data Upload Dialog */}
      <Dialog open={uploadHandlers.spendUpload.dialogOpen} onClose={uploadHandlers.spendUpload.handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon />
            Upload Spend Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>📊 Spend Data Upload</strong>
            </Typography>
            <Typography variant="body2">
              Upload actual spend data to compare against forecasts. The file should contain project codes, 
              monthly spend amounts, and company/CAPEX-OPEX classifications.
            </Typography>
          </Alert>
          
          {departments.length > 0 && (
            <TextField
              select
              label="Department (optional)"
              value={uploadHandlers.spendUpload.dept}
              onChange={(e) => uploadHandlers.spendUpload.setDept(e.target.value)}
              fullWidth
              margin="normal"
              disabled={uploadHandlers.spendUpload.uploading}
              helperText="Tag this upload to a specific department, or leave blank to apply to all."
              sx={{ mb: 2 }}
            >
              <MenuItem value="">All departments</MenuItem>
              {departments.map(d => <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>)}
            </TextField>
          )}

          <input
            type="file"
            accept=".xlsx,.xls,.csv"
                        onChange={(e) => {
              uploadHandlers.spendUpload.setUploadedFile(e.target.files[0]);
              uploadHandlers.spendUpload.setError('');
            }}
            style={{ marginBottom: '16px' }}
            disabled={uploadHandlers.spendUpload.uploading}
          />
          
                    {uploadHandlers.spendUpload.uploadedFile && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Selected: {uploadHandlers.spendUpload.uploadedFile.name} ({(uploadHandlers.spendUpload.uploadedFile.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
          
                    {uploadHandlers.spendUpload.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadHandlers.spendUpload.error}
            </Alert>
          )}
          {uploadHandlers.spendUpload.success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadHandlers.spendUpload.success}
            </Alert>
          )}
          
          {uploadHandlers.spendUpload.result && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Upload Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Processed Records:</strong> {uploadHandlers.spendUpload.result.processed_records || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Matched Outcomes:</strong> {uploadHandlers.spendUpload.result.saved_outcomes || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Total Amount:</strong> {formatCurrency(uploadHandlers.spendUpload.result.summary?.total_amount || 0)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Companies:</strong> {uploadHandlers.spendUpload.result.companies?.join(', ') || 'None'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
                        <DialogActions sx={{ p: 3, gap: 2 }}>
          <ActionButton 
            onClick={uploadHandlers.spendUpload.handleClose}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            {uploadHandlers.spendUpload.result ? 'Close' : 'Cancel'}
          </ActionButton>
          {!uploadHandlers.spendUpload.result && (
            <ActionButton 
              onClick={uploadHandlers.spendUpload.handleUpload} 
              variant="contained"
              disabled={!uploadHandlers.spendUpload.uploadedFile || uploadHandlers.spendUpload.uploading}
              startIcon={uploadHandlers.spendUpload.uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              sx={{ minWidth: 120 }}
            >
              {uploadHandlers.spendUpload.uploading ? 'Uploading...' : 'Upload'}
            </ActionButton>
          )}
        </DialogActions>
      </Dialog>

            {/* Forecast Data Upload Dialog */}
      <Dialog open={uploadHandlers.forecastUpload.dialogOpen} onClose={uploadHandlers.forecastUpload.handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon />
            Upload Forecast Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>📈 Forecast Data Upload</strong>
            </Typography>
            <Typography variant="body2">
              Upload official forecast data to compare against projected forecasts. The file should contain 
              project codes, monthly forecast amounts, and company/CAPEX-OPEX classifications.
            </Typography>
          </Alert>
          
          {departments.length > 0 && (
            <TextField
              select
              label="Department (optional)"
              value={uploadHandlers.forecastUpload.dept}
              onChange={(e) => uploadHandlers.forecastUpload.setDept(e.target.value)}
              fullWidth
              margin="normal"
              disabled={uploadHandlers.forecastUpload.uploading}
              helperText="Tag this upload to a specific department, or leave blank to apply to all."
              sx={{ mb: 2 }}
            >
              <MenuItem value="">All departments</MenuItem>
              {departments.map(d => <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>)}
            </TextField>
          )}

          <input
            type="file"
            accept=".xlsx,.xls,.csv"
                        onChange={(e) => {
              uploadHandlers.forecastUpload.setUploadedFile(e.target.files[0]);
              uploadHandlers.forecastUpload.setError('');
            }}
            style={{ marginBottom: '16px' }}
            disabled={uploadHandlers.forecastUpload.uploading}
          />
          
                    {uploadHandlers.forecastUpload.uploadedFile && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Selected: {uploadHandlers.forecastUpload.uploadedFile.name} ({(uploadHandlers.forecastUpload.uploadedFile.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
          
                    {uploadHandlers.forecastUpload.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadHandlers.forecastUpload.error}
            </Alert>
          )}
          {uploadHandlers.forecastUpload.success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadHandlers.forecastUpload.success}
            </Alert>
          )}
          
          {uploadHandlers.forecastUpload.result && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Upload Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                                      <strong>Processed Records:</strong> {uploadHandlers.forecastUpload.result.processed_records || 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Matched Outcomes:</strong> {uploadHandlers.forecastUpload.result.matched_outcomes || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                                      <strong>Total Amount:</strong> {formatCurrency(uploadHandlers.forecastUpload.result.total_amount || 0)}
                </Typography>
                <Typography variant="body2">
                  <strong>Companies:</strong> {uploadHandlers.forecastUpload.result.companies?.join(', ') || 'None'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
                        <DialogActions sx={{ p: 3, gap: 2 }}>
          <ActionButton 
            onClick={uploadHandlers.forecastUpload.handleClose}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            {uploadHandlers.forecastUpload.result ? 'Close' : 'Cancel'}
          </ActionButton>
          {!uploadHandlers.forecastUpload.result && (
            <ActionButton 
              onClick={uploadHandlers.forecastUpload.handleUpload} 
              variant="contained"
              color="secondary"
              disabled={!uploadHandlers.forecastUpload.uploadedFile || uploadHandlers.forecastUpload.uploading}
              startIcon={uploadHandlers.forecastUpload.uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              sx={{ minWidth: 120 }}
            >
              {uploadHandlers.forecastUpload.uploading ? 'Uploading...' : 'Upload'}
            </ActionButton>
          )}
        </DialogActions>
      </Dialog>

            {/* Advanced Options Dialog */}
      <Dialog open={uploadHandlers.advanced.dialogOpen} onClose={uploadHandlers.advanced.handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <GearIcon />
            Advanced Options
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Clear Uploaded Data</strong>
            </Typography>
            <Typography variant="body2">
              Permanently removes uploaded spend and/or forecast data. This action cannot be undone.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enter the password to proceed:
          </Typography>

          <TextField
            label="Password"
            type="password"
            value={uploadHandlers.advanced.password}
            onChange={(e) => {
              uploadHandlers.advanced.setPassword(e.target.value);
              uploadHandlers.advanced.setError('');
            }}
            fullWidth
            margin="normal"
            disabled={uploadHandlers.advanced.clearing}
          />

          {departments.length > 0 && (
            <TextField
              select
              label="Limit to department (optional)"
              value={uploadHandlers.advanced.clearDept || ''}
              onChange={(e) => uploadHandlers.advanced.setClearDept(e.target.value)}
              fullWidth
              margin="normal"
              disabled={uploadHandlers.advanced.clearing}
              helperText="Leave blank to clear across all departments."
            >
              <MenuItem value="">All departments</MenuItem>
              {departments.map(d => <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>)}
            </TextField>
          )}

          {uploadHandlers.advanced.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadHandlers.advanced.error}
            </Alert>
          )}
          {uploadHandlers.advanced.success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadHandlers.advanced.success}
            </Alert>
          )}

          {uploadHandlers.advanced.result && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Clear Data Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Cleared Files:</strong> {uploadHandlers.advanced.result.cleared_files?.join(', ') || 'None'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Message:</strong> {uploadHandlers.advanced.result.message || 'Operation completed'}
                  </Typography>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Next Steps:</strong> The data has been cleared from the server.
                  Click "Force Refresh" in the main interface to reload fresh data,
                  or close this dialog and click the "Force Refresh" button.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, flexWrap: 'wrap' }}>
          <ActionButton
            onClick={uploadHandlers.advanced.handleClose}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            {uploadHandlers.advanced.result ? 'Close' : 'Cancel'}
          </ActionButton>
          {!uploadHandlers.advanced.result && (
            <>
              <ActionButton
                onClick={() => uploadHandlers.advanced.handleClear('actuals', uploadHandlers.advanced.clearDept || null)}
                variant="outlined"
                color="error"
                disabled={!uploadHandlers.advanced.password || uploadHandlers.advanced.clearing}
                startIcon={uploadHandlers.advanced.clearing ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
                sx={{ minWidth: 150 }}
              >
                Clear Actuals
              </ActionButton>
              <ActionButton
                onClick={() => uploadHandlers.advanced.handleClear('forecast', uploadHandlers.advanced.clearDept || null)}
                variant="outlined"
                color="warning"
                disabled={!uploadHandlers.advanced.password || uploadHandlers.advanced.clearing}
                startIcon={uploadHandlers.advanced.clearing ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
                sx={{ minWidth: 150 }}
              >
                Clear Forecast
              </ActionButton>
              <ActionButton
                onClick={() => uploadHandlers.advanced.handleClear('all', uploadHandlers.advanced.clearDept || null)}
                variant="contained"
                color="error"
                disabled={!uploadHandlers.advanced.password || uploadHandlers.advanced.clearing}
                startIcon={uploadHandlers.advanced.clearing ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
                sx={{ minWidth: 150 }}
              >
                {uploadHandlers.advanced.clearing ? 'Clearing...' : 'Clear All'}
              </ActionButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ForecastVsActuals;